import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Textarea,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Collapse,
  IconButton,
  Badge,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useToast,
  Flex,
  Heading,
  Code,
  Link,
  Portal,
  Checkbox,
  CheckboxGroup,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Radio,
  RadioGroup,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  ChatIcon,
  ExternalLinkIcon,
  CopyIcon,
  CheckIcon,
  ArrowUpIcon,
  CloseIcon,
  RepeatIcon,
  HamburgerIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { BASE_URLS, SETTINGS_PASSWORD, prompts, data_column, file_reference_column, bucketName_files } from "./env";
import {
  fetchChatModels,
  chatModelOptions,
  coerceChatModel,
  defaultOcrModelsFrom,
} from "../utils/chatModels";
import { getSupabase, supabaseSchema } from "./supabaseClient";
import { classifyReportByFilename } from "../utils/reportProcessingUtils";
import {
  getMetadataColor,
  getMetadataBgColor,
  isHighlightedCategory,
} from "../utils/metadataColors";
import {
  normalizeRole,
  isInGroup,
  canAccessComponent,
  getLabel,
} from "../config/roles";
import { dbgLog } from "../utils/appDebugFlags";
import { withAIQueryTracking } from "../utils/userEventTracker";
import { loadAiChatSettings, saveAiChatSettings, DEFAULT_OCR_MODELS, CAVEMAN_INTENSITIES, skillBadgeLabel } from "../utils/aiChatSettings";

const aiLog = (...args) => dbgLog("ai-assistant", ...args);

/**
 * Returns the AI assistant welcome message based on user role and patient/entity data.
 */
function getWelcomeMessage(patientData, userRole, isMagicNotes = false) {
  if (isMagicNotes) {
    return `Hello! I'm your AI assistant for this document. I can help you find information from the document content and my general knowledge. What would you like to know?`;
  }
  const patient = Array.isArray(patientData) && patientData.length > 0
    ? patientData[0]
    : !Array.isArray(patientData) && patientData
      ? patientData
      : null;
  const name = patient?.["patient_name"] || patient?.["class_name"] || patient?.["client_name"] || patient?.["name"] || "Unknown";

  if (normalizeRole(userRole) === 'student') {
    return `Hello! I'm your AI assistant for class ${name}. I can help you find information from class materials, documents, and history. What would you like to know?`;
  }
  if (normalizeRole(userRole) === 'socialworker') {
    return `Hello! I'm your AI assistant for client ${name}. I can help you find information from their records, documents, and meeting history. What would you like to know?`;
  }
  return `Hello! I'm your AI assistant for patient ${name}. I can help you find information from their medical records, documents, and visit history. What would you like to know?`;
}

const AIAssistant = ({
  patientId,
  patientData,
  documentContent,
  isMinimized = false,
  onToggleMinimize,
  isGlobalPopup = false,
  onClose = null,
  onPopOut = null,
  globalPosition = null,
  onPositionChange = null,
  initialMessages = null,
  initialSelectedSources = null,
  initialThreadId = null,
  initialThreads = null,
  fullHeight = false,
  useExtractedData = false,
  initialReportFile = null,
  initialReportType = null,
  initialReportFileNames = null,
  effectiveDate = null,
  isMagicNotes = false,
}) => {
  // Initialize messages - don't load from sessionStorage on initial mount (page refresh)
  // Only load from sessionStorage when popup closes (handled in useEffect)
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const activeFetchController = useRef(null);
  const [isExpanded, setIsExpanded] = useState(!isMinimized);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const lastPatientIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const initialThreadIdRef = useRef(null); // Track initial thread ID to ensure it persists
  const hasAppliedInitialThreadIdRef = useRef(false); // Track if we've already applied the initial thread ID
  const modalMessagesContainerRef = useRef(null);
  const isMountedRef = useRef(true); // Track if component is mounted
  const pendingQueryProcessedRef = useRef(false); // Track if we've processed pending query for this mount
  const pollCleanupRef = useRef(null); // Store cleanup function for polling
  const [scrollTargetId, setScrollTargetId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const toast = useToast();

  // Role-based source identifiers (null-safe; userRole starts as null and loads async)
  const entityInfoSource = isInGroup(userRole, 'tenantSchema')
    ? `${getLabel(userRole, 'entity').toLowerCase().replace(' ', '_')}_info`
    : 'patient_info';
  const documentSource = isInGroup(userRole, 'tenantSchema') ? 'magic_notes' : 'medical_document';
  const historyLabel = getLabel(userRole, 'historyLabel');

  // Fetch current user role for role-specific welcome message
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.user?.id || cancelled) return;
        const { data } = await supabase
          .from("users")
          .select("role")
          .eq("id", sessionData.session.user.id)
          .single();
        if (!cancelled && data?.role) setUserRole(data.role);
      } catch (e) {
        if (!cancelled) console.warn("AIAssistant: could not fetch user role", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When userRole loads, remap default source names to role-specific ones
  // e.g. 'patient_info' → 'meeting_info', 'medical_document' → 'magic_notes' for social workers
  const prevEntityInfoSourceRef = useRef('patient_info');
  const prevDocumentSourceRef = useRef('medical_document');
  useEffect(() => {
    if (!userRole || initialSelectedSources) return;
    const prevEntitySrc = prevEntityInfoSourceRef.current;
    const prevDocSrc = prevDocumentSourceRef.current;
    if (entityInfoSource !== prevEntitySrc || documentSource !== prevDocSrc) {
      setSelectedSources(prev => prev.map(s => {
        if (s === prevEntitySrc) return entityInfoSource;
        if (s === prevDocSrc) return documentSource;
        return s;
      }));
      prevEntityInfoSourceRef.current = entityInfoSource;
      prevDocumentSourceRef.current = documentSource;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  const {
    isOpen: isPopOutOpen,
    onOpen: onPopOutOpen,
    onClose: onPopOutClose,
  } = useDisclosure();

  // Popup minimized state (for global popup)
  const [isPopupMinimized, setIsPopupMinimized] = useState(() => {
    const saved = sessionStorage.getItem("aiAssistantPopupMinimized");
    return saved === "true";
  });

  // Feedback state
  const [feedbackStatus, setFeedbackStatus] = useState({}); // { messageId: 'positive' | 'negative' }
  const [feedbackText, setFeedbackText] = useState({}); // { messageId: 'text' }
  const [showFeedbackInput, setShowFeedbackInput] = useState({}); // { messageId: true/false }
  const [submittingFeedback, setSubmittingFeedback] = useState({}); // { messageId: true/false }

  // Source selection state - use initialSelectedSources if provided, otherwise default to patient_info, medical_document, and ai_knowledge
  // Don't load from sessionStorage on initial mount (page refresh) - only load when popup closes (handled in useEffect)
  // Helper function to check if forms data exists (handles both array and object formats)
  const hasFormsData = () => {
    if (!patientData) return false;
    const patient =
      Array.isArray(patientData) && patientData.length > 0
        ? patientData[0]
        : !Array.isArray(patientData) && patientData
        ? patientData
        : null;
    return (
      patient?.submissions_array &&
      Array.isArray(patient.submissions_array) &&
      patient.submissions_array.length > 0
    );
  };

  // Helper function to check if files exist in a folder (same as PatientPage/EditableDocumentPage)
  const checkFilesExist = async (folderPath) => {
    if (!folderPath) return false;
    
    const supabase = await getSupabase();
    const BUCKET_NAME_LOCAL = bucketName_files && bucketName_files !== "undefined" && bucketName_files !== undefined ? bucketName_files : "files";
    
    try {
      // Parse the folder path to extract patient name
      const pathParts = folderPath.split('/');
      let basePath = '';
      let patientFolder = '';
      let dateFolder = '';
      
      if (pathParts.length >= 3) {
        basePath = pathParts.slice(0, -2).join('/'); // "New/Reports"
        patientFolder = pathParts[pathParts.length - 2]; 
        dateFolder = pathParts[pathParts.length - 1]; 
      }

      // Extract patient name (everything before the first underscore)
      const patientNameMatch = patientFolder ? patientFolder.match(/^(.+?)_/) : null;
      const patientName = patientNameMatch ? patientNameMatch[1] : patientFolder;

      const fileMap = new Map();

      // 1. Check the original folder path first
      const { data: dataOriginal, error: errorOriginal } = await supabase.storage
        .from(BUCKET_NAME_LOCAL)
        .list(folderPath);

      if (!errorOriginal && dataOriginal) {
        const filesOnly = dataOriginal.filter(item => item.name.includes('.'));
        filesOnly.forEach(file => {
          if (!fileMap.has(file.name)) {
            fileMap.set(file.name, file);
          }
        });
        if (fileMap.size > 0) return true; // Found files, return early
      }

      // 2. If no files found, check matching folders in base path
      if (basePath && patientName) {
        const { data: allFolders, error: listError } = await supabase.storage
          .from(BUCKET_NAME_LOCAL)
          .list(basePath);
        
        if (!listError && allFolders) {
          const patientNameLower = patientName.toLowerCase().trim();
          const normalizedPatientName = patientNameLower.replace(/\s+/g, ' ').replace(/\s*,\s*/g, ', ');
          
          const matchingFolders = allFolders.filter(folder => {
            if (folder.name.includes('.')) return false;
            const folderNameLower = folder.name.toLowerCase();
            const folderBaseName = folderNameLower.includes('_') 
              ? folderNameLower.split('_')[0].trim() 
              : '';
            const normalizedFolderBaseName = folderBaseName.replace(/\s+/g, ' ').replace(/\s*,\s*/g, ', ');
            return normalizedFolderBaseName === normalizedPatientName || 
                   folderNameLower.startsWith(`${normalizedPatientName}_`) ||
                   folderNameLower.startsWith(`${normalizedPatientName} `);
          });

          // Check each matching folder for files in the current date folder
          for (const folder of matchingFolders) {
            const patientFolderPath = `${basePath}/${folder.name}`;
            const { data: dateFolders, error: dateFoldersError } = await supabase.storage
              .from(BUCKET_NAME_LOCAL)
              .list(patientFolderPath);

            if (!dateFoldersError && dateFolders) {
              const currentDateFolders = dateFolders.filter(item => {
                if (item.name.includes('.')) return false;
                return item.name === dateFolder;
              });
              
              for (const dateFolderItem of currentDateFolders) {
                const fullDateFolderPath = `${patientFolderPath}/${dateFolderItem.name}`;
                const { data: dateFolderFiles, error: dateFolderError } = await supabase.storage
                  .from(BUCKET_NAME_LOCAL)
                  .list(fullDateFolderPath);

                if (!dateFolderError && dateFolderFiles) {
                  const filesOnly = dateFolderFiles.filter(f => f.name.includes('.'));
                  if (filesOnly.length > 0) return true; // Found files
                }
              }
            }
          }
        }
      }

      return fileMap.size > 0;
    } catch (error) {
      console.error("❌ AIAssistant: Error checking files existence:", error);
      return false;
    }
  };

  // Convert a base reports path to a date-specific path (MM-DD-YY folder)
  const getDateSpecificPath = (reportsPath, dateStr) => {
    if (!reportsPath || !dateStr) return reportsPath;
    const [year, month, day] = dateStr.split('-');
    const dateForPath = `${month}-${day}-${year.slice(2)}`; // MM-DD-YY
    const pathParts = reportsPath.split('/');
    if (pathParts.length >= 1) {
      const lastPart = pathParts[pathParts.length - 1];
      if (/^\d{2}-\d{2}-\d{2}$/.test(lastPart)) {
        pathParts[pathParts.length - 1] = dateForPath;
        return pathParts.join('/');
      } else if (pathParts.length >= 2) {
        const secondLastPart = pathParts[pathParts.length - 2];
        if (/^\d{2}-\d{2}-\d{2}$/.test(secondLastPart)) {
          pathParts[pathParts.length - 2] = dateForPath;
          return pathParts.join('/');
        } else {
          return `${reportsPath}/${dateForPath}`;
        }
      }
    }
    return reportsPath;
  };

  // Build a copy of patientData with the reports path corrected to effectiveDate
  const getEffectivePatientData = (pd) => {
    if (!effectiveDate || !pd) return pd;
    const correctEntry = (p) => {
      if (!p) return p;
      const raw = p[data_column] || p[file_reference_column];
      const corrected = getDateSpecificPath(raw, effectiveDate);
      if (!corrected || corrected === raw) return p;
      return { ...p, [data_column]: corrected, [file_reference_column]: corrected };
    };
    return Array.isArray(pd) ? pd.map(correctEntry) : correctEntry(pd);
  };

  // Fetch the list of report files from Supabase storage
  const fetchReportFiles = async (folderPath) => {
    if (!folderPath) return [];

    const supabase = await getSupabase();
    const BUCKET_NAME_LOCAL = bucketName_files && bucketName_files !== "undefined" && bucketName_files !== undefined ? bucketName_files : "files";

    try {
      const pathParts = folderPath.split('/');
      let basePath = '';
      let patientFolder = '';
      let dateFolder = '';

      if (pathParts.length >= 3) {
        basePath = pathParts.slice(0, -2).join('/');
        patientFolder = pathParts[pathParts.length - 2];
        dateFolder = pathParts[pathParts.length - 1];
      }

      const patientNameMatch = patientFolder ? patientFolder.match(/^(.+?)_/) : null;
      const patientName = patientNameMatch ? patientNameMatch[1] : patientFolder;

      const fileMap = new Map();

      const { data: dataOriginal, error: errorOriginal } = await supabase.storage
        .from(BUCKET_NAME_LOCAL)
        .list(folderPath);

      if (!errorOriginal && dataOriginal) {
        const filesOnly = dataOriginal.filter(item => item.name.includes('.'));
        filesOnly.forEach(file => {
          if (!fileMap.has(file.name)) fileMap.set(file.name, file);
        });
      }

      if (fileMap.size === 0 && basePath && patientName) {
        const { data: allFolders, error: listError } = await supabase.storage
          .from(BUCKET_NAME_LOCAL)
          .list(basePath);

        if (!listError && allFolders) {
          const patientNameLower = patientName.toLowerCase().trim();
          const normalizedPatientName = patientNameLower.replace(/\s+/g, ' ').replace(/\s*,\s*/g, ', ');

          const matchingFolders = allFolders.filter(folder => {
            if (folder.name.includes('.')) return false;
            const folderNameLower = folder.name.toLowerCase();
            const folderBaseName = folderNameLower.includes('_') ? folderNameLower.split('_')[0].trim() : '';
            const normalizedFolderBaseName = folderBaseName.replace(/\s+/g, ' ').replace(/\s*,\s*/g, ', ');
            return normalizedFolderBaseName === normalizedPatientName ||
                   folderNameLower.startsWith(`${normalizedPatientName}_`) ||
                   folderNameLower.startsWith(`${normalizedPatientName} `);
          });

          for (const folder of matchingFolders) {
            const patientFolderPath = `${basePath}/${folder.name}`;
            const { data: dateFolders, error: dateFoldersError } = await supabase.storage
              .from(BUCKET_NAME_LOCAL)
              .list(patientFolderPath);

            if (!dateFoldersError && dateFolders) {
              const currentDateFolders = dateFolders.filter(item => !item.name.includes('.') && item.name === dateFolder);
              for (const dateFolderItem of currentDateFolders) {
                const fullDateFolderPath = `${patientFolderPath}/${dateFolderItem.name}`;
                const { data: dateFolderFiles, error: dateFolderError } = await supabase.storage
                  .from(BUCKET_NAME_LOCAL)
                  .list(fullDateFolderPath);

                if (!dateFolderError && dateFolderFiles) {
                  const filesOnly = dateFolderFiles.filter(f => f.name.includes('.'));
                  filesOnly.forEach(file => {
                    if (!fileMap.has(file.name)) fileMap.set(file.name, file);
                  });
                }
              }
            }
          }
        }
      }

      return Array.from(fileMap.values());
    } catch (error) {
      console.error("❌ AIAssistant: Error fetching report files:", error);
      return [];
    }
  };

  // Check for reports when patientData changes
  useEffect(() => {
    const checkReports = async () => {
      if (!patientData) {
        setHasReports(false);
        return;
      }

      const patient =
        Array.isArray(patientData) && patientData.length > 0
          ? patientData[0]
          : !Array.isArray(patientData) && patientData
          ? patientData
          : null;

      // Check for reports using data_column or file_reference_column
      const rawReportsPath = patient?.[data_column] || patient?.[file_reference_column];
      // Apply date-specific path if effectiveDate is provided, so we don't show reports from the wrong date
      const reportsPath = effectiveDate ? getDateSpecificPath(rawReportsPath, effectiveDate) : rawReportsPath;
      aiLog("📋 AIAssistant checkReports:", {
        patientId,
        effectiveDate,
        rawReportsPath,
        resolvedReportsPath: reportsPath,
        patientName: patient?.name || patient?.[Object.keys(patient || {})[0]],
      });
      if (reportsPath) {
        const files = await fetchReportFiles(reportsPath);
        setHasReports(files.length > 0);
        setReportFiles(files);
        if (initialReportFileNames && initialReportFileNames.length > 0) {
          // Use explicitly passed file names (most accurate - from metadata-aware classification)
          const matching = files.filter(f => initialReportFileNames.includes(f.name));
          if (matching.length > 0) {
            setSelectedReportFiles(matching.map(f => f.name));
            setShowReportDropdown(true);
          }
        } else if (initialReportType) {
          // Fallback: select files matching this report type by filename
          const matching = files.filter(f => classifyReportByFilename(f.name) === initialReportType);
          if (matching.length > 0) {
            setSelectedReportFiles(matching.map(f => f.name));
            setShowReportDropdown(true);
          } else if (initialReportFile && files.some(f => f.name === initialReportFile)) {
            setSelectedReportFiles([initialReportFile]);
            setShowReportDropdown(true);
          }
          // else: no match found; documentContent carries the report text(s)
        } else if (initialReportFile) {
          setSelectedReportFiles([initialReportFile]);
          setShowReportDropdown(true);
        } else {
          setSelectedReportFiles(selectedSources.includes("uploaded_files") ? files.map(f => f.name) : []);
        }
      } else {
        setHasReports(false);
        setReportFiles([]);
      }
    };

    checkReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData, patientId, effectiveDate, initialReportType, initialReportFile, JSON.stringify(initialReportFileNames)]);

  const [selectedSources, setSelectedSources] = useState(() => {
    if (initialSelectedSources && Array.isArray(initialSelectedSources)) {
      return initialSelectedSources;
    }
    const defaultSources = isMagicNotes
      ? ["medical_document", "ai_knowledge"]
      : ["patient_info", "medical_document", "ai_knowledge"];
    // Check if forms data exists and add 'forms' to default sources
    // Note: This runs during initialization, so patientData might not be available yet
    // We'll add a useEffect to handle this reactively
    return defaultSources;
  });

  // Per-user "Default Sources" preference (persisted to the DB via
  // ai_chat_settings). null = "no explicit choice" → fall back to the
  // component's built-in default selection above. An array (even empty) means
  // an explicit choice. Stored as resolved role-specific source keys; a user's
  // role is stable, and unknown keys are filtered out gracefully on apply.
  const [defaultSources, setDefaultSources] = useState(null);
  // Auth user id resolved on mount; used to persist settings to the DB.
  const [authUserId, setAuthUserId] = useState(null);
  // Guards the one-time apply of defaultSources to the live selection so it
  // never clobbers the user's in-session toggles.
  const appliedDefaultSourcesRef = useRef(false);

  // Show/hide sources section
  const [showSources, setShowSources] = useState(true);

  // Show/hide feedback section
  const [showFeedback, setShowFeedback] = useState(true);

  // Track if patient has uploaded reports/files
  const [hasReports, setHasReports] = useState(false);
  const [reportFiles, setReportFiles] = useState([]); // List of all report files
  const [selectedReportFiles, setSelectedReportFiles] = useState([]); // empty = all selected
  const [showReportDropdown, setShowReportDropdown] = useState(false); // Toggle individual report dropdown

  // Cache key state for pre-processed sources
  const [cacheKey, setCacheKey] = useState(null);
  const [isPreparingSources, setIsPreparingSources] = useState(false);

  // Settings state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsPassword, setSettingsPassword] = useState("");
  const [settingsPasswordError, setSettingsPasswordError] = useState("");
  // Load settings from localStorage on mount
  const loadSettingsFromStorage = () => {
    try {
      const savedSpeedSetting = localStorage.getItem(
        "aiAssistant_speedSetting"
      );
      const savedCustomMaxCharsPerDoc = localStorage.getItem(
        "aiAssistant_customMaxCharsPerDoc"
      );
      const savedCustomMaxTotalLength = localStorage.getItem(
        "aiAssistant_customMaxTotalLength"
      );
      const savedIsSettingsUnlocked = localStorage.getItem(
        "aiAssistant_isSettingsUnlocked"
      );
      const savedChatModel = localStorage.getItem("aiAssistant_chatModel");
      const savedActiveSkill = localStorage.getItem("aiAssistant_activeSkill");
      const savedSkillIntensity = localStorage.getItem("aiAssistant_skillIntensity");

      return {
        speedSetting: savedSpeedSetting || "fast",
        customMaxCharsPerDoc: savedCustomMaxCharsPerDoc
          ? parseInt(savedCustomMaxCharsPerDoc, 10)
          : 5000,
        customMaxTotalLength: savedCustomMaxTotalLength
          ? parseInt(savedCustomMaxTotalLength, 10)
          : 50000,
        isSettingsUnlocked: savedIsSettingsUnlocked === "true",
        chatModel: savedChatModel ? parseInt(savedChatModel, 10) : 1, // Default to Sonnet 4.5 (1)
        activeSkill: savedActiveSkill === "caveman" ? "caveman" : null,
        skillIntensity: CAVEMAN_INTENSITIES.includes(savedSkillIntensity)
          ? savedSkillIntensity
          : "full",
      };
    } catch (e) {
      console.warn("Failed to load AI settings from localStorage:", e);
      return {
        speedSetting: "fast",
        customMaxCharsPerDoc: 5000,
        customMaxTotalLength: 50000,
        isSettingsUnlocked: false,
        chatModel: 1,
        activeSkill: null,
        skillIntensity: "full",
      };
    }
  };

  const initialSettings = loadSettingsFromStorage();
  const [speedSetting, setSpeedSetting] = useState(
    initialSettings.speedSetting
  ); // 'fast', 'medium', 'slow', 'advanced'
  const [customMaxCharsPerDoc, setCustomMaxCharsPerDoc] = useState(
    initialSettings.customMaxCharsPerDoc
  );
  const [customMaxTotalLength, setCustomMaxTotalLength] = useState(
    initialSettings.customMaxTotalLength
  );
  const [isSettingsUnlocked, setIsSettingsUnlocked] = useState(
    initialSettings.isSettingsUnlocked
  );
  const [chatModel, setChatModel] = useState(initialSettings.chatModel);
  const [activeSkill, setActiveSkill] = useState(initialSettings.activeSkill);
  const [skillIntensity, setSkillIntensity] = useState(initialSettings.skillIntensity);
  // Set the moment the user touches the Skill controls, so the async DB-settings
  // load below (which resolves after localStorage-seeded initial state, and can
  // land after a quick post-mount toggle) never stomps a choice the user already
  // made — that race was the source of the toggle "not taking effect" on the
  // first try.
  const skillTouchedRef = useRef(false);

  // Save chatModel to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("aiAssistant_chatModel", chatModel.toString());
    } catch (e) {
      console.warn("Failed to save chatModel to localStorage:", e);
    }
  }, [chatModel]);

  // Save activeSkill/skillIntensity to localStorage
  useEffect(() => {
    try {
      if (activeSkill) {
        localStorage.setItem("aiAssistant_activeSkill", activeSkill);
      } else {
        localStorage.removeItem("aiAssistant_activeSkill");
      }
      localStorage.setItem("aiAssistant_skillIntensity", skillIntensity);
    } catch (e) {
      console.warn("Failed to save skill settings to localStorage:", e);
    }
  }, [activeSkill, skillIntensity]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem("aiAssistant_speedSetting", speedSetting);
    } catch (e) {
      console.warn("Failed to save speedSetting to localStorage:", e);
    }
  }, [speedSetting]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "aiAssistant_customMaxCharsPerDoc",
        customMaxCharsPerDoc.toString()
      );
    } catch (e) {
      console.warn("Failed to save customMaxCharsPerDoc to localStorage:", e);
    }
  }, [customMaxCharsPerDoc]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "aiAssistant_customMaxTotalLength",
        customMaxTotalLength.toString()
      );
    } catch (e) {
      console.warn("Failed to save customMaxTotalLength to localStorage:", e);
    }
  }, [customMaxTotalLength]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "aiAssistant_isSettingsUnlocked",
        isSettingsUnlocked.toString()
      );
    } catch (e) {
      console.warn("Failed to save isSettingsUnlocked to localStorage:", e);
    }
  }, [isSettingsUnlocked]);

  // Default OCR models (Gemini 3 Flash → GPT-5.2 → Claude Opus) are imported from
  // ../utils/aiChatSettings as DEFAULT_OCR_MODELS to keep a single source of truth.

  // Model selection state - fetch models from AgenticAI backend
  const [availableModels, setAvailableModels] = useState([]);
  // Built-in chat models: the newest Claude models, fetched live from Anthropic
  // through the backend (plus what the legacy 0/1/2 choices map to).
  const [chatModelCatalog, setChatModelCatalog] = useState({
    chatModels: [],
    legacyMap: null,
    error: null,
    loading: true,
  });
  useEffect(() => {
    let cancelled = false;
    const djangoBaseUrl = (() => {
      const override = process.env.REACT_APP_BACKEND_URL;
      if (override && override.trim() !== "") return override;
      const hostname = window.location.hostname;
      return hostname === "localhost" || hostname === "127.0.0.1"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;
    })();
    fetchChatModels(djangoBaseUrl).then((r) => {
      if (!cancelled) {
        setChatModelCatalog({
          chatModels: r.chatModels,
          legacyMap: r.legacyMap,
          error: r.ok ? null : r.error,
          loading: false,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const [selectedModels, setSelectedModels] = useState(() => {
    try {
      const saved = localStorage.getItem("aiAssistant_selectedModels");
      if (saved) {
        return JSON.parse(saved);
      }
      // Backward compatibility
      const legacySaved = localStorage.getItem("aiAssistant_selectedModel");
      if (legacySaved) {
        return [legacySaved, legacySaved, legacySaved];
      }
      // Return default OCR models
      return DEFAULT_OCR_MODELS;
    } catch (e) {
      console.warn("Failed to parse saved models:", e);
      return DEFAULT_OCR_MODELS;
    }
  });
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelLoadError, setModelLoadError] = useState(null);

  // Fetch available models from AgenticAI backend
  const fetchAvailableModels = useCallback(async () => {
    try {
      setIsLoadingModels(true);
      setModelLoadError(null);

      const backendBaseUrl = (() => {
        // Get URL injected by backend (if available)
        let injectedUrl = window.ENV?.VERCEL_OCR_URL;
        // Check if it's the unreplaced template string (happens in local dev)
        if (injectedUrl && (injectedUrl.includes("{{") || injectedUrl === "")) {
          injectedUrl = null;
        }

        // Priority:
        // 1. window.ENV.VERCEL_OCR_URL (Backend injected)
        // 2. REACT_APP_VERCEL_OCR_URL (Frontend .env)
        // 3. VERCEL_OCR_URL (Backend .env passed to build)
        // 4. REACT_APP_AGENTICAI_URL (Legacy frontend)
        // 5. AGENTICAI_BASE_URL (Backend .env legacy)
        const override =
          injectedUrl ||
          process.env.REACT_APP_VERCEL_OCR_URL ||
          process.env.VERCEL_OCR_URL ||
          process.env.REACT_APP_AGENTICAI_URL ||
          process.env.AGENTICAI_BASE_URL;

        const hostname = window.location.hostname;
        let url =
          override && override.trim() !== ""
            ? override
            : hostname === "localhost" || hostname === "127.0.0.1"
            ? BASE_URLS.local.agenticai
            : BASE_URLS.render.agenticai;

        // Ensure robust URL cleaning
        return url.trim().replace(/\/+$/, "");
      })();

      // Debug log to verify URL construction
      aiLog(
        `[AIAssistant] Fetching models from: ${backendBaseUrl}/api/models`
      );

      const response = await fetch(`${backendBaseUrl}/api/models`);

      if (!response.ok) {
        throw new Error(`Failed to fetch models: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (contentType && !contentType.includes("application/json")) {
        console.warn(
          `[AIAssistant] Expected JSON but received ${contentType} from ${backendBaseUrl}/api/models`
        );
        const text = await response.text();
        console.warn(
          `[AIAssistant] Response body preview: ${text.substring(0, 200)}...`
        );
        throw new Error(
          `Invalid response format from server (expected JSON, got ${contentType})`
        );
      }

      const data = await response.json();
      const models = data.models || [];
      setAvailableModels(models);

      // Fill empty priority slots with the newest model of each provider.
      if (models.length > 0) {
        const defaults = defaultOcrModelsFrom(models);
        setSelectedModels((prev) => {
          const newModels = [...prev];
          let changed = false;
          [0, 1, 2].forEach((i) => {
            if (!newModels[i]) {
              newModels[i] = defaults[i] || defaults[0];
              changed = true;
            }
          });

          if (changed) {
            try {
              localStorage.setItem(
                "aiAssistant_selectedModels",
                JSON.stringify(newModels)
              );
            } catch (e) {
              console.warn("Failed to save selectedModels:", e);
            }
            return newModels;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error fetching models from AgenticAI:", error);
      setModelLoadError(error.message);
      // No hardcoded stand-ins: the error is shown in the settings panel.
      setAvailableModels([]);
    } finally {
      setIsLoadingModels(false);
    }
  }, []); // Remove dependency on selectedModels to avoid loop

  // Load models on mount
  useEffect(() => {
    fetchAvailableModels();
  }, [fetchAvailableModels]);

  // Save selected models to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        "aiAssistant_selectedModels",
        JSON.stringify(selectedModels)
      );
    } catch (e) {
      console.warn("Failed to save selectedModels to localStorage:", e);
    }
  }, [selectedModels]);

  // Canonical list of selectable sources for the CURRENT role. Built from the
  // SAME role-aware values the live source-selection UI uses (entityInfoSource,
  // documentSource, historyLabel, isMagicNotes) so the "Default Sources" tab
  // matches exactly. isMagicNotes hides the entity-info / history / reports /
  // audio rows (only document + AI knowledge remain), mirroring the chips.
  const getSelectableSources = useCallback(() => {
    const list = [];
    if (!isMagicNotes) list.push({ key: entityInfoSource, label: `${getLabel(userRole, 'entity')} Info` });
    list.push({ key: documentSource, label: isMagicNotes ? "Magic Notes" : getLabel(userRole, 'documentName') });
    if (!isMagicNotes) {
      list.push({ key: "uploaded_files", label: "Reports" });
      list.push({ key: "visit_history", label: historyLabel });
      if (canAccessComponent(userRole, 'aiAssistant.formsSource')) list.push({ key: "forms", label: "Forms" });
      list.push({ key: "audio_transcripts", label: "Audio/Transcripts" });
    }
    list.push({ key: "ai_knowledge", label: "AI Knowledge" });
    return list;
  }, [isMagicNotes, entityInfoSource, documentSource, historyLabel, userRole]);

  // Load per-user settings from the DB (users.ai_chat_settings) once on mount.
  // localStorage already seeded the initial state synchronously above; the DB is
  // the source of truth and reconciles here. Degrades silently to
  // localStorage/defaults when there's no session or the column doesn't exist.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const uid = session?.user?.id;
        if (cancelled || !uid) return;
        setAuthUserId(uid);
        const loaded = await loadAiChatSettings(supabase, uid);
        if (cancelled) return;
        setSpeedSetting(loaded.speedSetting);
        setCustomMaxCharsPerDoc(loaded.customMaxCharsPerDoc);
        setCustomMaxTotalLength(loaded.customMaxTotalLength);
        setChatModel(loaded.chatModel);
        if (!skillTouchedRef.current) {
          setActiveSkill(loaded.activeSkill);
          setSkillIntensity(loaded.skillIntensity);
        }
        if (Array.isArray(loaded.ocrModels) && loaded.ocrModels.length) {
          setSelectedModels(loaded.ocrModels);
        }
        setDefaultSources(loaded.defaultSources);
      } catch (e) {
        // Non-fatal — localStorage/defaults remain in effect.
        if (!cancelled) console.warn("AIAssistant: could not load ai_chat_settings", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply the saved default sources to the live selection ONCE, after they load.
  //   - null = no explicit choice → keep the built-in default selection.
  //   - Don't override when initialSelectedSources (the global-popup prop) is
  //     provided — that caller explicitly seeds the selection.
  //   - Set the guard FIRST so a later re-render can't apply a second time and
  //     clobber source toggles the user made in the meantime. The stored keys
  //     are already this user's role-specific keys, so no role-gated filtering
  //     is needed — a stale key (after a role change) is harmless: the chips and
  //     the query ignore sources that aren't valid for the current role.
  useEffect(() => {
    if (appliedDefaultSourcesRef.current || defaultSources == null) return;
    appliedDefaultSourcesRef.current = true;
    if (initialSelectedSources) return;
    setSelectedSources(defaultSources);
  }, [defaultSources, initialSelectedSources]);

  // Effective default-sources selection for the settings UI. null means "use the
  // component's built-in default selection" → show that as the starting checked
  // state (filtered to valid keys for the current role).
  const effectiveDefaultSources = useMemo(() => {
    const validKeys = getSelectableSources().map((s) => s.key);
    if (defaultSources != null) return defaultSources.filter((k) => validKeys.includes(k));
    const builtIn = isMagicNotes
      ? [documentSource, "ai_knowledge"]
      : [entityInfoSource, documentSource, "ai_knowledge"];
    return builtIn.filter((k) => validKeys.includes(k));
  // isMagicNotes/documentSource/entityInfoSource are already deps of
  // getSelectableSources (a useCallback), so its identity changes whenever they
  // do — listing them again is redundant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSources, getSelectableSources]);

  // Toggle a key's membership in the default-sources preference. The first edit
  // makes the choice explicit (an array, even empty) starting from the current
  // effective selection.
  const toggleDefaultSource = (key) => {
    setDefaultSources((prev) => {
      const base = prev != null ? prev : effectiveDefaultSources;
      return base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    });
  };

  // Persist the current AI chat settings to the DB (users.ai_chat_settings) +
  // localStorage cache. Shared by both Save-Settings buttons (draggable popup +
  // inline variant) to avoid duplicating the persistence logic. Never throws.
  const saveAiChatSettingsToDb = useCallback(async () => {
    try {
      const supabase = await getSupabase();
      return await saveAiChatSettings(supabase, authUserId, {
        speedSetting,
        customMaxCharsPerDoc,
        customMaxTotalLength,
        chatModel,
        ocrModels: selectedModels,
        defaultSources,
        activeSkill,
        skillIntensity,
      });
    } catch (e) {
      return { ok: false, error: e };
    }
  }, [authUserId, speedSetting, customMaxCharsPerDoc, customMaxTotalLength, chatModel, selectedModels, defaultSources, activeSkill, skillIntensity]);

  // ── Skill auto-persist ──────────────────────────────────────────────────
  // The Skill choice is a "set it once and it follows me" preference, so it is
  // written to users.ai_chat_settings the moment it's picked rather than
  // waiting on the Save Settings button (close the modal with ✕ and the choice
  // would otherwise have survived only in this device's localStorage).
  //   - skillTouchedRef gates it to real user edits, so the mount-time DB load
  //     never echoes back what it just read.
  //   - the signature ref keeps a change to any OTHER setting — which re-runs
  //     this effect through saveAiChatSettingsToDb's identity — from firing a
  //     redundant write.
  const lastSkillSaveRef = useRef(null);
  useEffect(() => {
    if (!skillTouchedRef.current || !authUserId) return;
    const signature = `${activeSkill || "none"}:${skillIntensity}`;
    if (lastSkillSaveRef.current === signature) return;
    lastSkillSaveRef.current = signature;
    saveAiChatSettingsToDb();
  }, [activeSkill, skillIntensity, authUserId, saveAiChatSettingsToDb]);

  // "Caveman · full" (or null) — drives the header badge on every chat variant.
  const activeSkillLabel = skillBadgeLabel(activeSkill, skillIntensity);

  // Header chip shown on each of the three chat shells (draggable popup, inline
  // docked bar, popped-out window) whenever a skill is rewriting replies.
  const renderSkillHeaderBadge = () =>
    activeSkillLabel ? (
      <Badge
        colorScheme="orange"
        fontSize="xs"
        data-testid="chat-skill-badge"
        title="Skill active — change it under Settings › Skills"
      >
        🪨 {activeSkillLabel}
      </Badge>
    ) : null;

  // Shared settings body (Tabs + Save button). Rendered identically inside both
  // the draggable-popup Modal and the inline Modal; extracted here to keep a
  // single source of truth. Closes over all the settings state/handlers above.
  const renderSettingsBody = () => (
    <>
      <Tabs variant="enclosed" isFitted>
        <TabList>
          <Tab>OCR Settings</Tab>
          <Tab>Text Chat</Tab>
          <Tab>Skills</Tab>
          <Tab>Default Sources</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Processing Speed</FormLabel>
                <RadioGroup
                  value={speedSetting}
                  onChange={setSpeedSetting}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="fast">
                      <Text fontSize="sm">
                        Fast (5,000 chars/doc, 50,000 total)
                      </Text>
                    </Radio>
                    <Radio value="medium">
                      <Text fontSize="sm">
                        Medium (10,000 chars/doc, 100,000 total)
                      </Text>
                    </Radio>
                    <Radio value="slow">
                      <Text fontSize="sm">
                        Slow (1,000,000 chars/doc, 1,000,000 total)
                      </Text>
                    </Radio>
                    <Radio value="advanced">
                      <Text fontSize="sm">
                        Advanced (Custom values)
                      </Text>
                    </Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {speedSetting === "advanced" && (
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Max Characters Per Document</FormLabel>
                    <NumberInput
                      value={customMaxCharsPerDoc}
                      onChange={(valueString) =>
                        setCustomMaxCharsPerDoc(
                          parseInt(valueString) || 5000
                        )
                      }
                      min={1000}
                      max={10000000}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Max Total Content Length</FormLabel>
                    <NumberInput
                      value={customMaxTotalLength}
                      onChange={(valueString) =>
                        setCustomMaxTotalLength(
                          parseInt(valueString) || 50000
                        )
                      }
                      min={10000}
                      max={10000000}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </VStack>
              )}

              {/* OCR Model Selection */}
              <FormControl>
                <FormLabel>OCR Model Priority</FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Select up to 3 models. If the first fails, the
                  next one will be used.
                </Text>
                {isLoadingModels ? (
                  <HStack>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.500">
                      Loading models...
                    </Text>
                  </HStack>
                ) : modelLoadError ? (
                  <Text fontSize="sm" color="red.500">
                    Failed to load models: {modelLoadError}
                  </Text>
                ) : (
                  <VStack spacing={3}>
                    {[0, 1, 2].map((index) => (
                      <FormControl key={index}>
                        <FormLabel fontSize="sm" color="gray.600">
                          Priority {index + 1}
                        </FormLabel>
                        <Select
                          value={selectedModels[index] || ""}
                          onChange={(e) => {
                            const newModels = [...selectedModels];
                            newModels[index] = e.target.value;
                            setSelectedModels(newModels);
                          }}
                          placeholder="Select a model"
                          size="sm"
                        >
                          {availableModels.map((model) => (
                            <option
                              key={model.model_id}
                              value={model.model_id}
                            >
                              {model.config?.display_name ||
                                model.model_id}
                              {model.config?.provider &&
                                ` (${model.config.provider})`}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    ))}
                  </VStack>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  mt={3}
                  onClick={() => {
                    const defaults = defaultOcrModelsFrom(availableModels);
                    if (!defaults.length) {
                      toast({
                        title: "Models not loaded",
                        description: "The live model list is unavailable, so there is nothing to reset to.",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                      });
                      return;
                    }
                    setSelectedModels(defaults);
                    const names = defaults
                      .map(
                        (id) =>
                          availableModels.find((m) => m.model_id === id)?.config
                            ?.display_name || id,
                      )
                      .join(", ");
                    toast({
                      title: "OCR Settings Reset",
                      description: `OCR models reset to the newest of each provider: ${names}`,
                      status: "info",
                      duration: 2000,
                      isClosable: true,
                    });
                  }}
                >
                  Reset to Default
                </Button>
              </FormControl>
            </VStack>
          </TabPanel>

          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Chat Model</FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Select the built-in model to use for text chat
                  queries.
                </Text>
                {(() => {
                  const { options, current } = chatModelOptions(
                    chatModelCatalog.chatModels,
                    chatModel,
                    chatModelCatalog.legacyMap,
                  );
                  if (chatModelCatalog.loading && !options.length) {
                    return (
                      <HStack>
                        <Spinner size="sm" />
                        <Text fontSize="sm" color="gray.500">
                          Loading the newest models…
                        </Text>
                      </HStack>
                    );
                  }
                  if (!options.length) {
                    return (
                      <Text fontSize="sm" color="red.500">
                        Failed to load chat models: {chatModelCatalog.error || "no models returned"}
                      </Text>
                    );
                  }
                  return (
                    <Select
                      value={current}
                      onChange={(e) =>
                        setChatModel(coerceChatModel(e.target.value) ?? current)
                      }
                    >
                      {options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  );
                })()}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Newest Claude models, fetched live from Anthropic.
                </Text>
              </FormControl>
            </VStack>
          </TabPanel>

          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Skill</FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Change how the assistant communicates. Applies to
                  every chat reply while active.
                </Text>
                <RadioGroup
                  value={activeSkill || "none"}
                  onChange={(v) => {
                    skillTouchedRef.current = true;
                    setActiveSkill(v === "none" ? null : v);
                  }}
                >
                  <VStack align="flex-start" spacing={2}>
                    <Radio value="none">
                      <Text fontSize="sm">None (default)</Text>
                    </Radio>
                    <Radio value="caveman">
                      <Text fontSize="sm">
                        Caveman (ultra-terse replies)
                      </Text>
                    </Radio>
                  </VStack>
                </RadioGroup>
              </FormControl>

              {activeSkill === "caveman" && (
                <FormControl>
                  <FormLabel>Intensity</FormLabel>
                  <RadioGroup
                    value={skillIntensity}
                    onChange={(v) => {
                      skillTouchedRef.current = true;
                      setSkillIntensity(v);
                    }}
                  >
                    <VStack align="flex-start" spacing={2}>
                      <Radio value="lite">
                        <Text fontSize="sm">
                          Lite — tight but full sentences
                        </Text>
                      </Radio>
                      <Radio value="full">
                        <Text fontSize="sm">
                          Full — classic caveman (default)
                        </Text>
                      </Radio>
                      <Radio value="ultra">
                        <Text fontSize="sm">
                          Ultra — maximum compression
                        </Text>
                      </Radio>
                    </VStack>
                  </RadioGroup>
                </FormControl>
              )}
            </VStack>
          </TabPanel>

          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Default Sources</FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Sources turned on automatically each time the
                  assistant loads.
                </Text>
                <CheckboxGroup
                  value={effectiveDefaultSources}
                  size="sm"
                >
                  <VStack align="flex-start" spacing={2}>
                    {getSelectableSources().map((src) => (
                      <Checkbox
                        key={src.key}
                        value={src.key}
                        isChecked={effectiveDefaultSources.includes(src.key)}
                        onChange={() => toggleDefaultSource(src.key)}
                      >
                        {src.label}
                      </Checkbox>
                    ))}
                  </VStack>
                </CheckboxGroup>
              </FormControl>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Button
        colorScheme="blue"
        onClick={() => {
          // Persist to the DB (users.ai_chat_settings) + localStorage
          // cache; fire-and-forget so the modal closes instantly.
          saveAiChatSettingsToDb();
          setIsSettingsOpen(false);
          toast({
            title: "Settings Saved",
            description: `Processing speed: ${speedSetting}${
              selectedModels[0]
                ? ` | Primary Model: ${
                    availableModels.find(
                      (m) => m.model_id === selectedModels[0]
                    )?.config?.display_name || selectedModels[0]
                  }`
                : ""
            }`,
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        }}
      >
        Save Settings
      </Button>
    </>
  );

  // Chat threads/tabs state
  const [threads, setThreads] = useState(() => initialThreads || []); // Array of { thread_id, last_message_time, first_message_preview }
  const [closedThreads, setClosedThreads] = useState(new Set()); // Set of thread_ids that are closed (hidden from tab bar but available in hamburger menu)
  const [activeThreadId, setActiveThreadId] = useState(
    () => initialThreadId || 1
  ); // Currently active thread
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [recentlyClosedThreads, setRecentlyClosedThreads] = useState(new Set()); // Set of thread IDs that are currently being closed (for animation)
  const [tabAnimationPhase, setTabAnimationPhase] = useState("idle"); // 'idle', 'shifting', 'resizing'
  const tabResizeTimeoutRefs = useRef(new Map()); // Map of threadId -> timeout ref for tracking individual timeouts

  // Draggable popup state - position on right side by default
  const [position, setPosition] = useState(() => {
    const defaultWidth = 600;
    const topBarHeight = 120;
    const rightSideX = window.innerWidth - defaultWidth - 20;
    const centerX = window.innerWidth / 2 - defaultWidth / 2;

    if (globalPosition) {
      // Check if globalPosition is centered - reset to right side
      const distanceFromCenter = Math.abs(globalPosition.x - centerX);
      if (distanceFromCenter < 200) {
        // Position is too close to center, use right side instead
        return { x: rightSideX, y: topBarHeight };
      }
      return globalPosition;
    }
    // Default to right side
    return {
      x: rightSideX,
      y: topBarHeight,
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const popupRef = useRef(null);
  const resizeStartState = useRef({ width: 0, height: 0, x: 0, y: 0 });
  const isResizingRef = useRef(false);
  const dragAnimationFrameRef = useRef(null);
  const pendingDragPosition = useRef(null);
  const resizeAnimationFrameRef = useRef(null);
  const pendingResizeState = useRef(null);
  const hasManuallyMovedRef = useRef(false); // Track if user has manually moved/resized

  // Resizable popup size state - default to side panel size
  const getDefaultSize = () => {
    const topBarHeight = 120;
    const sideWidth = 500;
    const sideHeight = window.innerHeight - topBarHeight - 20;
    return { width: sideWidth, height: sideHeight };
  };

  const [popupSize, setPopupSize] = useState(() => {
    // Always start with default size - will be reset on open for global popup
    return getDefaultSize();
  });

  // Handle resize start - capture initial state
  const handleResizeStart = (e, data) => {
    isResizingRef.current = true;
    resizeStartState.current = {
      width: popupSize.width,
      height: popupSize.height,
      x: position.x,
      y: position.y,
    };
  };

  // Handle resize during drag - update position in real-time with direct DOM updates
  const handleResize = (e, data) => {
    const startState = resizeStartState.current;
    const newSize = {
      width: data.size.width,
      height: data.size.height,
    };

    // Update position when resizing from handles that affect position
    // ResizableBox maintains top-left corner, but we want opposite corner to stay fixed
    const handle = data.handle;
    let newX = startState.x;
    let newY = startState.y;

    if (handle === "sw" || handle === "nw") {
      // Southwest or Northwest: when width increases, move top-left left (negative X)
      // When width decreases, move top-left right (positive X)
      const widthChange = newSize.width - startState.width;
      newX = startState.x - widthChange;
    }
    if (handle === "ne" || handle === "nw") {
      // Northeast or Northwest: when height increases, move top-left up (negative Y)
      // When height decreases, move top-left down (positive Y)
      const heightChange = newSize.height - startState.height;
      newY = startState.y - heightChange;
    }

    // Update position directly in DOM for instant, responsive resizing (proportional to cursor movement)
    // This avoids React re-renders that cause inconsistent performance
    if (popupRef.current) {
      popupRef.current.style.left = `${newX}px`;
      popupRef.current.style.top = `${newY}px`;
    }

    // DON'T update state during resize - this causes inconsistent performance due to React re-renders
    // ResizableBox handles its own size updates internally during resize
    // We'll sync state only when resize stops

    // Store both size and position for final sync on resize stop
    pendingDragPosition.current = {
      x: newX,
      y: newY,
      width: newSize.width,
      height: newSize.height,
    };
  };

  // Handle resize stop - save to sessionStorage and sync state
  const handleResizeStop = (e, data) => {
    isResizingRef.current = false;

    // Mark as manually moved since user is resizing
    hasManuallyMovedRef.current = true;

    // Get final values from pending state or calculate
    let finalSize, finalPosition;
    if (pendingDragPosition.current && pendingDragPosition.current.width) {
      // Use stored values from resize
      finalSize = {
        width: pendingDragPosition.current.width,
        height: pendingDragPosition.current.height,
      };
      finalPosition = {
        x: pendingDragPosition.current.x,
        y: pendingDragPosition.current.y,
      };
    } else {
      // Fallback: use data from ResizableBox
      finalSize = {
        width: data.size.width,
        height: data.size.height,
      };
      if (pendingDragPosition.current) {
        finalPosition = {
          x: pendingDragPosition.current.x,
          y: pendingDragPosition.current.y,
        };
      } else {
        // Fallback: calculate position if pending wasn't set
        const startState = resizeStartState.current;
        const handle = data.handle;
        let newX = startState.x;
        let newY = startState.y;

        if (handle === "sw" || handle === "nw") {
          const widthChange = finalSize.width - startState.width;
          newX = startState.x - widthChange;
        }
        if (handle === "ne" || handle === "nw") {
          const heightChange = finalSize.height - startState.height;
          newY = startState.y - heightChange;
        }

        finalPosition = { x: newX, y: newY };
      }
    }

    // Sync state with final values (DOM already updated during resize)
    setPopupSize(finalSize);
    setPosition(finalPosition);
    if (isGlobalPopup && onPositionChange) {
      onPositionChange(finalPosition);
    }

    pendingDragPosition.current = null;
    sessionStorage.setItem("aiAssistantPopupSize", JSON.stringify(finalSize));
  };

  // Handle popup minimize/maximize toggle
  const handleTogglePopupMinimize = () => {
    const newMinimizedState = !isPopupMinimized;
    setIsPopupMinimized(newMinimizedState);
    sessionStorage.setItem(
      "aiAssistantPopupMinimized",
      String(newMinimizedState)
    );
  };

  // Check if we're on the document page
  const isOnDocumentPage = useCallback(() => {
    // Check for the white document container that's specific to EditableDocumentPage
    // Try multiple selectors to be robust
    const documentContainer =
      document.querySelector(".bg-white.min-h.max-w-full") ||
      document.querySelector(
        '[class*="bg-white"][class*="min-h"][class*="max-w-full"]'
      ) ||
      document.querySelector(".main-content-area .bg-white");
    return !!documentContainer;
  }, []);

  // Get the right edge position of the document and calculate width to extend to page edge
  const getDocumentDockingInfo = useCallback(() => {
    // Try multiple selectors to be robust
    const documentContainer =
      document.querySelector(".bg-white.min-h.max-w-full") ||
      document.querySelector(
        '[class*="bg-white"][class*="min-h"][class*="max-w-full"]'
      ) ||
      document.querySelector(".main-content-area .bg-white");
    if (!documentContainer) return null;

    const rect = documentContainer.getBoundingClientRect();
    const documentRightEdge = rect.right;

    // Calculate width to extend all the way to the right edge of the screen
    const pageRightEdge = window.innerWidth;
    const width = pageRightEdge - documentRightEdge - 10; // 10px gap from document, extend to screen edge

    return {
      x: documentRightEdge + 10, // Start position with 10px gap
      width: Math.max(400, width), // Minimum width of 400px
    };
  }, []);

  // Move popup to side position - dock to document if on document page, otherwise to screen edge
  const handleMoveToSide = () => {
    const topBarHeight = 120; // Account for top navigation bar - lower position
    const sideHeight = window.innerHeight - topBarHeight - 20; // Full height minus top bar and margin

    let newPosition;
    let newWidth;

    // Check if we're on the document page
    if (isOnDocumentPage()) {
      const dockingInfo = getDocumentDockingInfo();
      if (dockingInfo) {
        // Dock to the right of the document, extending to page edge
        newPosition = {
          x: dockingInfo.x,
          y: topBarHeight, // Position below top bar
        };
        newWidth = dockingInfo.width;
      } else {
        // Fallback to screen edge if document not found
        const sideWidth = 500;
        newPosition = {
          x: window.innerWidth - sideWidth - 20,
          y: topBarHeight,
        };
        newWidth = sideWidth;
      }
    } else {
      // Not on document page - use screen edge
      const sideWidth = 500;
      newPosition = {
        x: window.innerWidth - sideWidth - 20, // Right side with 20px margin
        y: topBarHeight, // Position below top bar
      };
      newWidth = sideWidth;
    }

    setPosition(newPosition);
    setPopupSize({ width: newWidth, height: sideHeight });
    sessionStorage.setItem(
      "aiAssistantPopupSize",
      JSON.stringify({ width: newWidth, height: sideHeight })
    );

    // Mark as manually moved (via button click) so auto-dock doesn't override
    hasManuallyMovedRef.current = true;

    // Update global position if this is a global popup
    if (isGlobalPopup && onPositionChange) {
      onPositionChange(newPosition);
    }
  };

  // Reset size to default when popup opens (for global popup)
  const hasInitializedSizeRef = useRef(false);
  useEffect(() => {
    if (isGlobalPopup && !hasInitializedSizeRef.current) {
      // Reset to default size when popup first opens
      const topBarHeight = 120;
      const sideWidth = 500;
      const sideHeight = window.innerHeight - topBarHeight - 20;
      const defaultSize = { width: sideWidth, height: sideHeight };
      setPopupSize(defaultSize);
      sessionStorage.setItem(
        "aiAssistantPopupSize",
        JSON.stringify(defaultSize)
      );
      hasInitializedSizeRef.current = true;
      // Reset manual movement flag so it can auto-dock on fresh open
      hasManuallyMovedRef.current = false;
    } else if (!isGlobalPopup) {
      // Reset flags when popup closes
      hasInitializedSizeRef.current = false;
      hasManuallyMovedRef.current = false;
    }
  }, [isGlobalPopup]);

  // Update position when globalPosition changes (for global popup)
  useEffect(() => {
    if (isGlobalPopup && globalPosition) {
      setPosition(globalPosition);
    }
  }, [isGlobalPopup, globalPosition]);

  // Auto-dock to document when navigating to document page (only initially, before manual movement)
  useEffect(() => {
    if (!isGlobalPopup) return; // Only for global popup
    if (hasManuallyMovedRef.current) return; // Don't auto-dock if user has manually moved/resized

    // Check if we're on the document page and dock to document
    const checkAndDock = () => {
      if (hasManuallyMovedRef.current) return; // Exit if user manually moved

      if (isOnDocumentPage()) {
        const dockingInfo = getDocumentDockingInfo();
        if (dockingInfo) {
          const topBarHeight = 120;
          const sideHeight = window.innerHeight - topBarHeight - 20;
          const newPosition = {
            x: dockingInfo.x,
            y: topBarHeight,
          };

          // Only update if position would be different (avoid unnecessary updates)
          const currentX = position.x;
          const expectedX = dockingInfo.x;

          // Check if we're not already docked (within 50px tolerance)
          if (Math.abs(currentX - expectedX) > 50) {
            setPosition(newPosition);
            setPopupSize({ width: dockingInfo.width, height: sideHeight });
            if (onPositionChange) {
              onPositionChange(newPosition);
            }
          }
        }
      }
    };

    // Check immediately
    checkAndDock();

    // Also check after a delay to catch document loading
    const timeoutId = setTimeout(checkAndDock, 500);

    // Check periodically in case document position changes (e.g., sidebar toggle)
    // But only if user hasn't manually moved
    const intervalId = setInterval(() => {
      if (!hasManuallyMovedRef.current) {
        checkAndDock();
      }
    }, 1000);

    // Also check on window resize (but only if not manually moved)
    const handleResize = () => {
      if (!hasManuallyMovedRef.current) {
        checkAndDock();
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    isGlobalPopup,
    position.x,
    onPositionChange,
    isOnDocumentPage,
    getDocumentDockingInfo,
  ]);

  // Sync DOM position with state when not dragging/resizing
  useEffect(() => {
    if (!isDragging && !isResizingRef.current && popupRef.current) {
      popupRef.current.style.left = `${position.x}px`;
      popupRef.current.style.top = `${position.y}px`;
    }
  }, [position, isDragging]);

  // Drag handlers - only drag from header
  const handleHeaderMouseDown = (e) => {
    // Don't drag if clicking on buttons or interactive elements
    if (e.target.closest('button, input, textarea, a, [role="button"]')) return;
    e.preventDefault();
    setIsDragging(true);
    const rect = popupRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      // No constraints - allow free movement anywhere
      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;

      // Update DOM directly for instant, smooth movement (no React re-renders during drag)
      if (popupRef.current) {
        popupRef.current.style.left = `${newX}px`;
        popupRef.current.style.top = `${newY}px`;
      }

      // Store position for state sync on mouse up
      pendingDragPosition.current = { x: newX, y: newY };
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // Mark as manually moved since user is dragging
      hasManuallyMovedRef.current = true;

      // Cancel any pending animation frame
      if (dragAnimationFrameRef.current) {
        cancelAnimationFrame(dragAnimationFrameRef.current);
        dragAnimationFrameRef.current = null;
      }

      // Sync final position to React state (DOM already updated)
      if (pendingDragPosition.current) {
        const finalPosition = pendingDragPosition.current;
        setPosition(finalPosition);
        if (isGlobalPopup && onPositionChange) {
          onPositionChange(finalPosition);
        }
        pendingDragPosition.current = null;
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        // Cleanup animation frame
        if (dragAnimationFrameRef.current) {
          cancelAnimationFrame(dragAnimationFrameRef.current);
          dragAnimationFrameRef.current = null;
        }
      };
    }
  }, [
    isDragging,
    dragOffset,
    popupSize,
    isPopupMinimized,
    isGlobalPopup,
    onPositionChange,
  ]);

  // No auto-scrolling - user controls scrolling manually
  const scrollToBottom = () => {
    try {
      if (messagesContainerRef && messagesContainerRef.current) {
        const el = messagesContainerRef.current;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        return;
      }
      if (messagesEndRef && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    } catch (e) {
      // ignore
    }
  };

  // Auto-scroll behavior based on who sent the last message
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (!last) return;
    if (last.type === "user") {
      // After user sends a message, keep the input visible (scroll to bottom)
      scrollToBottom();
    } else if (last.type === "assistant") {
      // After assistant responds, scroll to sources if available, otherwise to message box
      if (last.sources && last.sources.length > 0) {
        setScrollTargetId(`ai-msg-sources-${last.id}`);
      } else {
        setScrollTargetId(`ai-msg-${last.id}`);
      }
    }
  }, [messages]);

  // Scroll the selected assistant message to top of the container
  useEffect(() => {
    if (!scrollTargetId) return;
    try {
      const el = document.getElementById(scrollTargetId);
      const container = messagesContainerRef?.current;
      if (el && container) {
        // Compute position of the target element relative to the scroll container
        const targetTop = el.offsetTop - container.offsetTop;
        container.scrollTo({ top: targetTop, behavior: "smooth" });
      }
    } catch (e) {
      // ignore
    } finally {
      setScrollTargetId(null);
    }
  }, [scrollTargetId]);

  // Track if we've initialized with initialMessages to avoid overwriting later
  const hasInitializedWithMessagesRef = useRef(false);
  const hasAutoSentInitialMessageRef = useRef(false);

  // Load threads for the patient from database
  const loadThreads = useCallback(async () => {
    if (!patientId) return;

    try {
      setIsLoadingThreads(true);
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userJwtToken = session?.access_token || null;

      if (!userJwtToken) {
        console.warn("No JWT token available for loading threads");
        return;
      }

      const backendBaseUrl = (() => {
        const override = process.env.REACT_APP_BACKEND_URL;
        if (override && override.trim() !== "") return override;
        const hostname = window.location.hostname;
        return hostname === "localhost" || hostname === "127.0.0.1"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;
      })();

      // NOTE: this endpoint just loads the thread list; not a user-issued AI query.
      // Tracking moved to the actual /query/ call sites below.
      const response = await fetch(`${backendBaseUrl}/api/ai-assistant/threads/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(userJwtToken ? { Authorization: `Bearer ${userJwtToken}` } : {}),
        },
        body: JSON.stringify({
          patientId: patientId,
          schema: supabaseSchema,
          userJwtToken: userJwtToken,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const dbThreads = data.threads || [];

        // Load empty threads from localStorage for persistence across page refreshes
        let storedEmptyThreads = [];
        try {
          const storageKey = `ai_assistant_empty_threads_${patientId}`;
          storedEmptyThreads = JSON.parse(
            localStorage.getItem(storageKey) || "[]"
          );
        } catch (storageError) {
          console.warn(
            "Failed to load empty threads from localStorage:",
            storageError
          );
        }

        // Merge database threads with existing empty threads in state and stored empty threads
        setThreads((prev) => {
          // Create a map of thread_id -> thread from database
          const dbThreadMap = new Map();
          dbThreads.forEach((thread) => {
            dbThreadMap.set(thread.thread_id, thread);
          });

          // Get max thread_id from database threads (threads with messages)
          const maxDbThreadId =
            dbThreads.length > 0
              ? Math.max(...dbThreads.map((t) => t.thread_id || 1))
              : 0;

          // Combine empty threads from state and localStorage
          const allEmptyThreads = [
            ...prev.filter(
              (t) => !t.last_message_time && !dbThreadMap.has(t.thread_id)
            ),
            ...storedEmptyThreads.filter((t) => !dbThreadMap.has(t.thread_id)),
          ];

          // Remove duplicates from empty threads
          const uniqueEmptyThreads = Array.from(
            new Map(allEmptyThreads.map((t) => [t.thread_id, t])).values()
          );

          // Filter empty threads: only remove if they're the last thread (highest thread_id)
          // Find the maximum thread_id from all threads (database + empty)
          const allThreadIds = [
            ...dbThreads.map((t) => t.thread_id || 1),
            ...uniqueEmptyThreads.map((t) => t.thread_id || 1),
          ];
          const maxThreadId =
            allThreadIds.length > 0 ? Math.max(...allThreadIds) : 0;

          const validEmptyThreads = uniqueEmptyThreads.filter((emptyThread) => {
            // If there are no database threads, keep all empty threads
            if (dbThreads.length === 0) {
              return true;
            }

            // Only remove empty thread if it's the last thread (highest thread_id overall)
            // Keep empty threads that are not the maximum thread_id
            return emptyThread.thread_id !== maxThreadId;
          });

          // Update localStorage with valid empty threads
          try {
            const storageKey = `ai_assistant_empty_threads_${patientId}`;
            localStorage.setItem(storageKey, JSON.stringify(validEmptyThreads));
          } catch (storageError) {
            console.warn(
              "Failed to update empty threads in localStorage:",
              storageError
            );
          }

          // Combine: database threads + valid empty threads
          // Put database threads first so they override empty threads with same thread_id
          const allThreads = [...dbThreads, ...validEmptyThreads];

          // Remove duplicates by thread_id, keeping the first occurrence
          // Since dbThreads come first, database threads will override empty threads
          const uniqueThreads = Array.from(
            new Map(allThreads.map((t) => [t.thread_id, t])).values()
          );

          // Sort by thread_id
          uniqueThreads.sort((a, b) => a.thread_id - b.thread_id);

          // If no threads exist at all, ensure thread 1 exists
          if (uniqueThreads.length === 0) {
            return [
              {
                thread_id: 1,
                last_message_time: null,
                first_message_preview: "",
              },
            ];
          }

          return uniqueThreads;
        });
      } else {
        console.error("Failed to load threads:", await response.text());
        // Even if API fails, ensure at least thread 1 exists
        setThreads((prev) => {
          if (prev.length === 0) {
            return [
              {
                thread_id: 1,
                last_message_time: null,
                first_message_preview: "",
              },
            ];
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error loading threads:", error);
      // Even if there's an error, ensure at least thread 1 exists
      setThreads((prev) => {
        if (prev.length === 0) {
          return [
            {
              thread_id: 1,
              last_message_time: null,
              first_message_preview: "",
            },
          ];
        }
        return prev;
      });
    } finally {
      setIsLoadingThreads(false);
    }
  }, [patientId]);

  // Load chat history for a specific thread
  const loadChatHistory = useCallback(
    async (threadId) => {
      if (!patientId || !threadId) return;

      try {
        setIsLoadingHistory(true);
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userJwtToken = session?.access_token || null;

        if (!userJwtToken) {
          console.warn("No JWT token available for loading chat history");
          return;
        }

        const backendBaseUrl = (() => {
          const override = process.env.REACT_APP_BACKEND_URL;
          if (override && override.trim() !== "") return override;
          const hostname = window.location.hostname;
          return hostname === "localhost" || hostname === "127.0.0.1"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;
        })();

        const response = await fetch(
          `${backendBaseUrl}/api/ai-assistant/chat-history/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(userJwtToken ? { Authorization: `Bearer ${userJwtToken}` } : {}),
            },
            body: JSON.stringify({
              patientId: patientId,
              threadId: threadId,
              schema: supabaseSchema,
              userJwtToken: userJwtToken,
            }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          const historyMessages = data.messages || [];

          if (historyMessages.length > 0) {
            // Convert timestamps to Date objects
            const formattedMessages = historyMessages.map((msg) => ({
              ...msg,
              timestamp: new Date(msg.timestamp),
            }));
            setMessages(formattedMessages);
          } else {
            // No history, show welcome message
            setMessages([
              {
                id: "welcome",
                type: "assistant",
                content: getWelcomeMessage(patientData, userRole, isMagicNotes),
                timestamp: new Date(),
                sources: [],
              },
            ]);
          }
        } else {
          console.error("Failed to load chat history:", await response.text());
          // Fallback to welcome message
          setMessages([
            {
              id: "welcome",
              type: "assistant",
              content: getWelcomeMessage(patientData, userRole, isMagicNotes),
              timestamp: new Date(),
              sources: [],
            },
          ]);
        }
      } catch (error) {
        console.error("Error loading chat history:", error);
        // Fallback to welcome message
        setMessages([
          {
            id: "welcome",
            type: "assistant",
            content: getWelcomeMessage(patientData, userRole, isMagicNotes),
            timestamp: new Date(),
            sources: [],
          },
        ]);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [patientId, patientData, userRole]
  );

  // Create a new thread
  const createNewThread = useCallback(() => {
    if (!patientId) return;

    try {
      // Calculate next thread_id locally from current threads state
      // This ensures unique IDs even when creating multiple threads quickly
      // We use a functional update to get the latest threads state
      setThreads((prev) => {
        // Find the maximum thread_id from existing threads
        const maxThreadId =
          prev.length > 0 ? Math.max(...prev.map((t) => t.thread_id || 1)) : 0;

        // New thread_id is max + 1 (or 1 if no threads exist)
        let newThreadId = maxThreadId + 1;

        // Check if this thread_id already exists (shouldn't happen, but safety check)
        while (prev.some((t) => t.thread_id === newThreadId)) {
          newThreadId++;
        }

        // Add new thread to threads list
        const newThread = {
          thread_id: newThreadId,
          last_message_time: null,
          first_message_preview: "",
        };

        // Save empty threads to localStorage for persistence across page refreshes
        try {
          const storageKey = `ai_assistant_empty_threads_${patientId}`;
          const existingEmptyThreads = JSON.parse(
            localStorage.getItem(storageKey) || "[]"
          );
          // Add new empty thread if it doesn't already exist
          if (!existingEmptyThreads.some((t) => t.thread_id === newThreadId)) {
            existingEmptyThreads.push(newThread);
            localStorage.setItem(
              storageKey,
              JSON.stringify(existingEmptyThreads)
            );
          }
        } catch (storageError) {
          console.warn(
            "Failed to save empty thread to localStorage:",
            storageError
          );
        }

        // Update other state synchronously (React will batch these updates)
        setActiveThreadId(newThreadId);
        setMessages([
          {
            id: "welcome",
            type: "assistant",
            content: getWelcomeMessage(patientData, userRole, isMagicNotes),
            timestamp: new Date(),
            sources: [],
          },
        ]);

        return [...prev, newThread];
      });
    } catch (error) {
      console.error("Error creating new thread:", error);
      toast({
        title: "Error",
        description: "Failed to create new chat thread",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [patientId, patientData, userRole, toast]);

  // Switch to a different thread (and reopen if it was closed)
  const switchThread = useCallback(
    (threadId) => {
      // If thread was closed, reopen it
      if (closedThreads.has(threadId)) {
        setClosedThreads((prev) => {
          const newSet = new Set(prev);
          newSet.delete(threadId);
          return newSet;
        });
      }
      // Mark that user has manually switched threads, so we shouldn't force initialThreadId anymore
      hasAppliedInitialThreadIdRef.current = true;
      setActiveThreadId(threadId);
      loadChatHistory(threadId);
    },
    [closedThreads, loadChatHistory]
  );

  // Close a thread (hide from tab bar but keep available in hamburger menu)
  const closeThread = useCallback(
    (threadId, e) => {
      e.stopPropagation(); // Prevent switching to the thread when clicking X

      // Don't allow closing if already being closed
      if (recentlyClosedThreads.has(threadId)) {
        return;
      }

      // Clear ALL existing timeouts to reset the delay for all closing tabs
      tabResizeTimeoutRefs.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      tabResizeTimeoutRefs.current.clear();

      // Start animation: first phase is shifting (tabs with higher numbers shift left)
      setRecentlyClosedThreads((prev) => {
        const newSet = new Set([...prev, threadId]);
        setTabAnimationPhase("shifting");

        // Create new timeouts for all threads that are being closed (including the new one)
        // This resets the 2-second delay for all of them
        newSet.forEach((closingThreadId) => {
          const timeoutId = setTimeout(() => {
            setTabAnimationPhase("resizing");
            // Actually mark as closed now (hide from DOM)
            setClosedThreads(
              (prevClosed) => new Set([...prevClosed, closingThreadId])
            );
            // Remove from recently closed set
            setRecentlyClosedThreads((prevRecent) => {
              const updatedSet = new Set(prevRecent);
              updatedSet.delete(closingThreadId);
              // Clean up timeout ref
              tabResizeTimeoutRefs.current.delete(closingThreadId);
              // Reset animation state if no more threads are being closed
              if (updatedSet.size === 0) {
                setTimeout(() => {
                  setTabAnimationPhase("idle");
                }, 300); // Allow transition to complete
              }
              return updatedSet;
            });
          }, 2000);

          // Store timeout ref for this thread
          tabResizeTimeoutRefs.current.set(closingThreadId, timeoutId);
        });

        return newSet;
      });

      // If we closed the active thread, switch to another one
      if (activeThreadId === threadId) {
        // Find the next available (non-closed) thread
        const openThreads = threads.filter(
          (t) => !closedThreads.has(t.thread_id) && t.thread_id !== threadId
        );
        if (openThreads.length > 0) {
          // Switch to the last open thread
          const nextThread = openThreads[openThreads.length - 1];
          setActiveThreadId(nextThread.thread_id);
          loadChatHistory(nextThread.thread_id);
        } else {
          // No open threads left, create thread 1 if it doesn't exist
          const thread1Exists = threads.some((t) => t.thread_id === 1);
          if (thread1Exists) {
            // Reopen thread 1
            setClosedThreads((prev) => {
              const newSet = new Set(prev);
              newSet.delete(1);
              return newSet;
            });
            setActiveThreadId(1);
            loadChatHistory(1);
          } else {
            // Create new thread 1
            setActiveThreadId(1);
            setMessages([
              {
                id: "welcome",
                type: "assistant",
                content: getWelcomeMessage(patientData, userRole, isMagicNotes),
                timestamp: new Date(),
                sources: [],
              },
            ]);
          }
        }
      }
    },
    [
      activeThreadId,
      threads,
      closedThreads,
      recentlyClosedThreads,
      patientData,
      userRole,
      loadChatHistory,
    ]
  );

  // Reopen a closed thread (make it visible in tab bar again)
  const reopenThread = useCallback(
    (threadId) => {
      setClosedThreads((prev) => {
        const newSet = new Set(prev);
        newSet.delete(threadId);
        return newSet;
      });
      setActiveThreadId(threadId);
      loadChatHistory(threadId);
    },
    [loadChatHistory]
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts for all threads
      tabResizeTimeoutRefs.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      tabResizeTimeoutRefs.current.clear();
    };
  }, []);

  // Set initial thread ID and threads immediately when provided (for pop-out)
  // This must run first to ensure correct tab selection
  useEffect(() => {
    // If initial threads are provided (from pop-out), use them immediately
    if (initialThreads && initialThreads.length > 0) {
      setThreads(initialThreads);
    }

    // If initial thread ID is provided (from pop-out), use it immediately
    // This must happen before any other logic to ensure correct tab selection
    // Only apply once on mount, not on every prop change
    if (initialThreadId && !hasAppliedInitialThreadIdRef.current) {
      aiLog(
        "🔍 AIAssistant: Setting initial thread ID to",
        initialThreadId
      );
      initialThreadIdRef.current = initialThreadId; // Store in ref for persistence
      setActiveThreadId(initialThreadId);
      hasAppliedInitialThreadIdRef.current = true; // Mark as applied
    }
  }, [initialThreadId, initialThreads]); // Only depend on these props

  // Load threads and history on patient change
  useEffect(() => {
    // Skip loading chat history if initialMessages are provided (they take precedence)
    if (
      initialMessages &&
      Array.isArray(initialMessages) &&
      initialMessages.length > 0
    ) {
      aiLog(
        "🔍 AIAssistant: Skipping chat history load because initialMessages are provided",
        {
          initialThreadId,
          hasInitialThreads: !!(initialThreads && initialThreads.length > 0),
          currentActiveThreadId: activeThreadId,
        }
      );
      // Still load threads if not provided, but preserve activeThreadId
      if (!initialThreads || initialThreads.length === 0) {
        loadThreads().then(() => {
          // After loading threads, ensure activeThreadId is still correct
          const threadIdToUse = initialThreadIdRef.current || initialThreadId;
          if (threadIdToUse) {
            aiLog(
              "🔍 AIAssistant: Restoring active thread ID after loadThreads:",
              threadIdToUse
            );
            setActiveThreadId(threadIdToUse);
          }
        });
      } else {
        // Even if threads are provided, ensure activeThreadId is set
        // But only if we haven't already applied it (to allow user to switch threads)
        const threadIdToUse = initialThreadIdRef.current || initialThreadId;
        if (
          threadIdToUse &&
          !hasAppliedInitialThreadIdRef.current &&
          activeThreadId !== threadIdToUse
        ) {
          aiLog(
            "🔍 AIAssistant: Ensuring active thread ID matches initial:",
            threadIdToUse
          );
          setActiveThreadId(threadIdToUse);
          hasAppliedInitialThreadIdRef.current = true; // Mark as applied
        }
      }
      // Load history for the correct thread (if not already loaded via initialMessages)
      if (initialThreadId && initialMessages.length === 0) {
        loadChatHistory(initialThreadId);
      }
      return;
    }

    if (patientId && lastPatientIdRef.current !== patientId) {
      // Patient changed, reset and load threads
      lastPatientIdRef.current = patientId;
      hasInitializedWithMessagesRef.current = false;
      // Only reset to thread 1 if no initial thread ID provided
      if (!initialThreadId) {
        setActiveThreadId(1);
      }
      loadThreads().then(() => {
        // After loading threads, ensure activeThreadId is still correct
        const threadIdToUse = initialThreadIdRef.current || initialThreadId;
        if (threadIdToUse) {
          aiLog(
            "🔍 AIAssistant: Restoring active thread ID after patient change:",
            threadIdToUse
          );
          setActiveThreadId(threadIdToUse);
        }
        // Load history for active thread
        loadChatHistory(threadIdToUse || activeThreadId || 1);
      });
    } else if (patientId && !hasInitializedWithMessagesRef.current) {
      // First load for this patient
      hasInitializedWithMessagesRef.current = true;
      loadThreads().then(() => {
        // After loading threads, ensure activeThreadId is still correct
        const threadIdToUse = initialThreadIdRef.current || initialThreadId;
        if (threadIdToUse) {
          aiLog(
            "🔍 AIAssistant: Restoring active thread ID after first load:",
            threadIdToUse
          );
          setActiveThreadId(threadIdToUse);
        }
        loadChatHistory(threadIdToUse || activeThreadId);
      });
    }
  }, [
    patientId,
    initialMessages,
    initialThreadId,
    initialThreads,
    activeThreadId,
    loadThreads,
    loadChatHistory,
  ]); // Include all dependencies

  // Save messages and selectedSources to sessionStorage when they change (for global popup)
  // This allows the inline component to restore the chat when popup closes
  useEffect(() => {
    if (isGlobalPopup && patientId && messages.length > 0) {
      try {
        sessionStorage.setItem(
          `aiAssistantMessages_${patientId}`,
          JSON.stringify(messages)
        );
      } catch (e) {
        console.warn("Failed to save messages to sessionStorage:", e);
      }
    }
  }, [messages, isGlobalPopup, patientId]);

  useEffect(() => {
    // Don't persist context-specific sources (set via initialSelectedSources, e.g. audio processing)
    // to sessionStorage — they're only meaningful for that session and would pollute the next
    // inline chat session (causing "Audio/Transcripts" to appear selected instead of "Magic Notes").
    if (isGlobalPopup && patientId && selectedSources.length > 0 && !initialSelectedSources) {
      try {
        sessionStorage.setItem(
          `aiAssistantSelectedSources_${patientId}`,
          JSON.stringify(selectedSources)
        );
      } catch (e) {
        console.warn("Failed to save selectedSources to sessionStorage:", e);
      }
    }
  }, [selectedSources, isGlobalPopup, patientId, initialSelectedSources]);

  // Track component mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Check for pending queries when component mounts and poll for responses
  // Wait a bit for initialMessages to be set first
  useEffect(() => {
    if (!patientId || pendingQueryProcessedRef.current) return;

    // Small delay to ensure initialMessages are set first
    const timeoutId = setTimeout(() => {
      const pendingQueryKey = `aiAssistantPendingQuery_${patientId}`;
      const pendingQueryStr = sessionStorage.getItem(pendingQueryKey);

      if (pendingQueryStr) {
        try {
          const pendingQuery = JSON.parse(pendingQueryStr);
          const queryId = pendingQuery.queryId;

          // Mark as processed to avoid duplicate processing
          pendingQueryProcessedRef.current = true;

          // Use functional update to check current messages state (avoids stale closure)
          setMessages((prev) => {
            // Check if user message is already in messages (by ID or by content + timestamp)
            const hasUserMessage = prev.some(
              (m) =>
                m.id === pendingQuery.userMessageId ||
                (m.type === "user" &&
                  m.content === pendingQuery.prompt &&
                  Math.abs(
                    new Date(m.timestamp).getTime() -
                      new Date(pendingQuery.timestamp).getTime()
                  ) < 2000)
            );

            // If user message is missing, add it
            if (!hasUserMessage) {
              const userMessage = {
                id: pendingQuery.userMessageId,
                type: "user",
                content: pendingQuery.prompt,
                timestamp: new Date(pendingQuery.timestamp),
              };
              return [...prev, userMessage];
            }
            return prev;
          });

          // Set loading state
          setIsLoading(true);

          // Poll for response
          const pollInterval = setInterval(() => {
            const responseKey = `aiAssistantResponse_${queryId}`;
            const responseStr = sessionStorage.getItem(responseKey);

            if (responseStr) {
              try {
                const responseData = JSON.parse(responseStr);
                const assistantMessage = responseData.assistantMessage;

                // Use functional update to avoid stale closure
                setMessages((prev) => {
                  // Check if assistant message is already in messages
                  const hasAssistantMessage = prev.some(
                    (m) =>
                      m.type === "assistant" &&
                      m.prompt === pendingQuery.prompt &&
                      Math.abs(
                        new Date(m.timestamp).getTime() -
                          new Date(responseData.timestamp).getTime()
                      ) < 5000
                  );

                  if (!hasAssistantMessage) {
                    if (responseData.isError) {
                      toast({
                        title: "Error",
                        description: "Failed to get response from AI assistant",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                    return [...prev, assistantMessage];
                  }
                  return prev;
                });

                // Clear pending query and response from sessionStorage
                sessionStorage.removeItem(pendingQueryKey);
                sessionStorage.removeItem(responseKey);
                setIsLoading(false);
                clearInterval(pollInterval);
              } catch (e) {
                console.error("Error parsing response from sessionStorage:", e);
                sessionStorage.removeItem(responseKey);
                setIsLoading(false);
                clearInterval(pollInterval);
              }
            }
          }, 500); // Poll every 500ms

          // Timeout after 60 seconds
          const timeout = setTimeout(() => {
            clearInterval(pollInterval);
            setIsLoading(false);
            sessionStorage.removeItem(pendingQueryKey);
            console.warn("Timeout waiting for query response");
          }, 60000);

          // Store cleanup function in ref
          pollCleanupRef.current = () => {
            clearInterval(pollInterval);
            clearTimeout(timeout);
          };
        } catch (e) {
          console.error("Error parsing pending query from sessionStorage:", e);
          sessionStorage.removeItem(pendingQueryKey);
        }
      }
    }, 100); // Small delay to let initialMessages set first

    return () => {
      clearTimeout(timeoutId);
      // Clean up polling if component unmounts
      if (pollCleanupRef.current) {
        pollCleanupRef.current();
        pollCleanupRef.current = null;
      }
    };
  }, [patientId, toast]); // Removed messages from deps to avoid re-running on every message change

  // Reset pending query processed flag when patientId changes
  useEffect(() => {
    pendingQueryProcessedRef.current = false;
    hasAutoSentInitialMessageRef.current = false; // Reset auto-send flag when patient changes
  }, [patientId]);

  // Handle initialMessages and initialSelectedSources - MUST run first to prevent chat history from overwriting
  useEffect(() => {
    // Set selectedSources if initialSelectedSources is provided (takes precedence over everything)
    // This must run for both global popups and inline components
    if (
      initialSelectedSources &&
      Array.isArray(initialSelectedSources) &&
      initialSelectedSources.length > 0
    ) {
      aiLog(
        "🔍 AIAssistant: Setting selectedSources from initialSelectedSources:",
        initialSelectedSources
      );
      setSelectedSources(initialSelectedSources);
    }

    // Only process messages if component is mounted and we haven't already processed these initialMessages
    if (!isMountedRef.current || hasAutoSentInitialMessageRef.current) return;

    if (
      initialMessages &&
      Array.isArray(initialMessages) &&
      initialMessages.length > 0
    ) {
      aiLog("🔍 AIAssistant: Processing initialMessages", {
        messageCount: initialMessages.length,
        firstMessageType: initialMessages[0]?.type,
        isGlobalPopup,
        patientId,
      });

      // Mark as initialized immediately to prevent chat history from overwriting
      hasInitializedWithMessagesRef.current = true;
      hasAutoSentInitialMessageRef.current = true;

      // Set messages from initialMessages
      setMessages(initialMessages);

      // Check if the last message is a user message without an assistant response
      // This handles the case where a user sent a message but the component was closed before getting a response
      const lastMessage = initialMessages[initialMessages.length - 1];

      // Check if there's an assistant response after the last user message
      // Look for assistant messages that come after the last user message
      const lastUserMessageIndex = initialMessages
        .map((m, i) => ({ m, i }))
        .filter(({ m }) => m.type === "user")
        .pop()?.i;

      const hasAssistantResponse =
        lastUserMessageIndex !== undefined &&
        initialMessages
          .slice(lastUserMessageIndex + 1)
          .some((m) => m.type === "assistant");

      // Only auto-send if the last message is a user message without a corresponding assistant response
      // Allow auto-send for global popups too (e.g., Process form button opens new session with initial message)
      if (
        lastMessage &&
        lastMessage.type === "user" &&
        lastMessage.content &&
        !hasAssistantResponse
      ) {
        aiLog(
          "🔍 AIAssistant: Auto-sending last user message (no assistant response found)"
        );
        // Increased delay to ensure popup is fully rendered
        const autoSendTimeout = setTimeout(() => {
          if (!isMountedRef.current) {
            console.warn(
              "⚠️ AIAssistant: Component unmounted before auto-send, cancelling"
            );
            return;
          }
          // Directly call the send logic instead of setting input
          const promptText = lastMessage.content;
          const queryId = `query_${patientId}_${Date.now()}`;

          // Don't add duplicate user message since it's already in initialMessages
          setIsLoading(true);

          // Store pending query info
          const pendingQueryKey = `aiAssistantPendingQuery_${patientId}`;
          sessionStorage.setItem(
            pendingQueryKey,
            JSON.stringify({
              queryId: queryId,
              prompt: promptText,
              userMessageId: lastMessage.id,
              timestamp: lastMessage.timestamp
                ? typeof lastMessage.timestamp === "string"
                  ? lastMessage.timestamp
                  : lastMessage.timestamp.toISOString()
                : new Date().toISOString(),
            })
          );

          // Call the API directly (similar to handleSendMessage but without adding user message again)
          const sendInitialMessage = async () => {
            aiLog("🔍 AIAssistant: sendInitialMessage called", {
              promptText: promptText.substring(0, 100) + "...",
            });
            try {
              // Get user ID, username, and JWT token from Supabase for auto-save feature
              const supabase = await getSupabase();
              const {
                data: { session },
              } = await supabase.auth.getSession();
              const userJwtToken = session?.access_token || null;
              const userId = session?.user?.id || null;
              const username =
                session?.user?.user_metadata?.username ||
                session?.user?.email ||
                "Unknown User";

              // Get patient name and scheduled date for auto-save
              const patient =
                Array.isArray(patientData) && patientData.length > 0
                  ? patientData[0]
                  : !Array.isArray(patientData) && patientData
                  ? patientData
                  : null;
              const patientName =
                patient?.[prompts[0][0]] ||
                patient?.patient_name ||
                "Unknown Patient";
              const scheduledDate =
                patient?.date_ || new Date().toISOString().split("T")[0];

              const backendBaseUrl = (() => {
                const override = process.env.REACT_APP_BACKEND_URL;
                if (override && override.trim() !== "") return override;
                const hostname = window.location.hostname;
                return hostname === "localhost" || hostname === "127.0.0.1"
                  ? BASE_URLS.local.backend
                  : BASE_URLS.render.backend;
              })();

              // Get truncation parameters - need to call getTruncationParameters after it's defined
              // For now, use loadSettingsFromStorage to get custom values
              const settings = loadSettingsFromStorage();
              const truncationParams = {
                maxCharsPerDocument: settings.customMaxCharsPerDoc || 5000,
                maxTotalContentLength: settings.customMaxTotalLength || 50000,
              };

              const controller = new AbortController();
              activeFetchController.current = controller;

              // For the auto-send, selectedReportFiles state may not be populated yet
              // (checkReports() is async). Use initialReportFileNames directly if available.
              const autoSendReportFiles = (() => {
                if (initialReportFileNames && initialReportFileNames.length > 0) {
                  return initialReportFileNames;
                }
                return selectedReportFiles.length < reportFiles.length ? selectedReportFiles : null;
              })();

              aiLog(
                "🔍 AIAssistant: Sending auto-send POST request to /api/ai-assistant/query/",
                {
                  patientId,
                  selectedSources: initialSelectedSources || selectedSources,
                  promptLength: promptText.length,
                  selectedReportFiles: autoSendReportFiles,
                  reportFileCount: autoSendReportFiles?.length ?? "all",
                }
              );

              // Create a timeout for the fetch request (5 minutes for OCR processing)
              const fetchTimeout = setTimeout(() => {
                if (controller && !controller.signal.aborted) {
                  console.warn(
                    "⏱️ AIAssistant: Fetch request timeout after 5 minutes, aborting..."
                  );
                  controller.abort();
                }
              }, 300000); // 5 minutes timeout for OCR processing

              // Include Epic clinical documents if available for this patient
              let epicDocuments = null;
              try {
                const raw = sessionStorage.getItem(`epicDocs_${patientId}`);
                if (raw) epicDocuments = JSON.parse(raw);
              } catch (_) {}

              const response = await withAIQueryTracking('assistant', () => fetch(
                `${backendBaseUrl}/api/ai-assistant/query/`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    ...(userJwtToken ? { Authorization: `Bearer ${userJwtToken}` } : {}),
                  },
                  signal: controller.signal,
                  body: JSON.stringify({
                    query: promptText,
                    patientId: patientId,
                    patientData: getEffectivePatientData(patientData),
                    documentContent: documentContent,
                    sessionId: `session_${patientId}_${Date.now()}`,
                    userId: userId,
                    username: username,
                    patientName: patientName,
                    scheduledDate: scheduledDate,
                    selectedSources: initialSelectedSources || selectedSources,
                    selectedReportFiles: autoSendReportFiles,
                    cacheKey: cacheKey,
                    schema: supabaseSchema,
                    userJwtToken: userJwtToken,
                    userRole: userRole,
                    maxCharsPerDocument: truncationParams.maxCharsPerDocument,
                    maxTotalContentLength:
                      truncationParams.maxTotalContentLength,
                    useExtractedData: useExtractedData || false,
                    epicDocuments: epicDocuments,
                    activeSkill: activeSkill,
                    skillIntensity: skillIntensity,
                  }),
                }
              ));

              // Clear the timeout since we got a response
              clearTimeout(fetchTimeout);

              aiLog("🔍 AIAssistant: Received response", {
                status: response.status,
                ok: response.ok,
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error("❌ AIAssistant: API error response", {
                  status: response.status,
                  errorText,
                });
                throw new Error(
                  `HTTP error! status: ${
                    response.status
                  }, message: ${errorText.substring(0, 200)}`
                );
              }

              const data = await response.json();
              aiLog("🔍 AIAssistant: Response data received", {
                hasAnswer: !!data.answer,
                hasResponse: !!data.response,
                answerLength: data.answer?.length || 0,
              });

              activeFetchController.current = null;

              const assistantMessage = {
                id: (Date.now() + 1).toString(),
                type: "assistant",
                content: data.answer || data.response || "No response received",
                prompt: promptText,
                timestamp: new Date(),
                sources: data.sources || [],
                activeSkill: data.activeSkill || null,
                skillIntensity: data.skillIntensity || null,
              };

              aiLog("🔍 AIAssistant: Adding assistant message to chat", {
                messageId: assistantMessage.id,
              });

              if (isMountedRef.current) {
                setMessages((prev) => {
                  aiLog(
                    "🔍 AIAssistant: Current messages count:",
                    prev.length
                  );
                  return [...prev, assistantMessage];
                });
                setIsLoading(false);
              }

              // Clear pending query
              sessionStorage.removeItem(pendingQueryKey);
              aiLog("✅ AIAssistant: Auto-send completed successfully");
            } catch (error) {
              if (error.name === "AbortError") {
                aiLog("Request was aborted");
              } else {
                console.error("❌ Error sending initial message:", error);
                if (isMountedRef.current) {
                  toast({
                    title: "Error",
                    description: `Failed to send message: ${error.message}`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                  });
                }
              }
              if (isMountedRef.current) {
                setIsLoading(false);
              }
              activeFetchController.current = null;
            }
          };

          aiLog("🔍 AIAssistant: Starting auto-send after delay");
          sendInitialMessage().catch((error) => {
            console.error(
              "❌ AIAssistant: Error in sendInitialMessage promise:",
              error
            );
            if (isMountedRef.current) {
              toast({
                title: "Error",
                description: `Failed to send initial message: ${error.message}`,
                status: "error",
                duration: 5000,
                isClosable: true,
              });
              setIsLoading(false);
            }
          });
        }, 1000); // Increased delay to ensure popup is fully rendered and stable

        return () => {
          clearTimeout(autoSendTimeout);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessages, initialSelectedSources]); // Only depend on initialMessages and initialSelectedSources to prevent re-runs

  // Separate useEffect to ensure initialSelectedSources is always applied (runs whenever it changes)
  // This ensures it takes precedence over any sessionStorage values
  useEffect(() => {
    if (
      initialSelectedSources &&
      Array.isArray(initialSelectedSources) &&
      initialSelectedSources.length > 0
    ) {
      aiLog(
        "🔍 AIAssistant: Applying initialSelectedSources (separate effect):",
        initialSelectedSources
      );
      setSelectedSources(initialSelectedSources);
      // Clear sessionStorage to prevent it from overriding on next render
      if (patientId) {
        try {
          sessionStorage.removeItem(`aiAssistantSelectedSources_${patientId}`);
          aiLog(
            "🔍 AIAssistant: Cleared sessionStorage for selectedSources to prevent override"
          );
        } catch (e) {
          console.warn("Failed to clear sessionStorage:", e);
        }
      }
    }
  }, [initialSelectedSources, patientId]);

  // Track if we've checked for saved state (to avoid loading on page refresh)
  const hasCheckedSavedStateRef = useRef(false);

  // For inline component: Load saved messages and selectedSources from sessionStorage when popup closes
  // But NOT on page refresh - only when transitioning from popup to inline
  useEffect(() => {
    // If initialSelectedSources is provided, always use it and skip sessionStorage
    // This must take absolute precedence over any sessionStorage values
    if (
      initialSelectedSources &&
      Array.isArray(initialSelectedSources) &&
      initialSelectedSources.length > 0
    ) {
      aiLog(
        "🔍 AIAssistant: initialSelectedSources provided, using it instead of sessionStorage:",
        initialSelectedSources
      );
      setSelectedSources(initialSelectedSources);
      // Clear sessionStorage to prevent it from overriding on next render
      if (patientId) {
        try {
          sessionStorage.removeItem(`aiAssistantSelectedSources_${patientId}`);
          aiLog(
            "🔍 AIAssistant: Cleared sessionStorage for selectedSources (inline component)"
          );
        } catch (e) {
          console.warn("Failed to clear sessionStorage:", e);
        }
      }
      return; // Don't load from sessionStorage if initialSelectedSources is provided
    }

    if (!isGlobalPopup && patientId && !hasCheckedSavedStateRef.current) {
      // Check if there's a flag indicating the popup was just closed (not a page refresh)
      // We'll set this flag when the popup closes
      const popupJustClosed = sessionStorage.getItem(
        `aiAssistantPopupJustClosed_${patientId}`
      );

      if (popupJustClosed === "true") {
        // Popup was just closed, restore the chat
        const savedMessages = sessionStorage.getItem(
          `aiAssistantMessages_${patientId}`
        );
        const savedSelectedSources = sessionStorage.getItem(
          `aiAssistantSelectedSources_${patientId}`
        );

        if (savedMessages) {
          try {
            const parsed = JSON.parse(savedMessages);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setMessages(parsed);
            }
          } catch (e) {
            console.warn("Failed to parse saved messages:", e);
          }
        }

        // Load from sessionStorage only if initialSelectedSources was not provided
        if (savedSelectedSources) {
          try {
            const parsed = JSON.parse(savedSelectedSources);
            if (Array.isArray(parsed) && parsed.length > 0) {
              aiLog(
                "🔍 AIAssistant: Loading selectedSources from sessionStorage:",
                parsed
              );
              setSelectedSources(parsed);
            }
          } catch (e) {
            console.warn("Failed to parse saved selectedSources:", e);
          }
        }

        // Clear the flag after loading
        sessionStorage.removeItem(`aiAssistantPopupJustClosed_${patientId}`);
      } else {
        // Page refresh - clear any saved state to start fresh
        sessionStorage.removeItem(`aiAssistantMessages_${patientId}`);
        sessionStorage.removeItem(`aiAssistantSelectedSources_${patientId}`);
      }

      hasCheckedSavedStateRef.current = true;
    }
  }, [isGlobalPopup, patientId, initialSelectedSources]);

  // Handle close - save state before closing (for global popup)
  const handleClose = () => {
    if (isGlobalPopup && patientId) {
      // Save current state to sessionStorage before closing
      try {
        sessionStorage.setItem(
          `aiAssistantMessages_${patientId}`,
          JSON.stringify(messages)
        );
        sessionStorage.setItem(
          `aiAssistantSelectedSources_${patientId}`,
          JSON.stringify(selectedSources)
        );
        // Set flag to indicate popup was just closed (not a page refresh)
        sessionStorage.setItem(
          `aiAssistantPopupJustClosed_${patientId}`,
          "true"
        );
      } catch (e) {
        console.warn("Failed to save state to sessionStorage on close:", e);
      }
    }
    if (onClose) {
      onClose();
    }
  };

  // Note: hasFormsData is now a function defined earlier in the component

  // Prepare sources on page load (optimization - pre-process all sources once)
  // Only prepare if we don't already have a cache key for this patient
  // If processing is already in progress (from another instance), wait for it to complete
  useEffect(() => {
    // Handle both array and object formats for patientData
    const hasPatientData =
      patientData &&
      ((Array.isArray(patientData) && patientData.length > 0) ||
        (!Array.isArray(patientData) && patientData !== null));
    if (!patientId || !hasPatientData) return;

    // Check if we already have a cache key stored for this patient
    const storedCacheKey = sessionStorage.getItem(
      `aiAssistantCacheKey_${patientId}`
    );
    if (storedCacheKey) {
      // We already have a cache key, use it without re-processing
      setCacheKey(storedCacheKey);
      aiLog(`✅ Using existing cache for patient ${patientId}`);
      return;
    }

    // Check if processing is already in progress (from another instance - inline or popup)
    const isProcessingKey = `aiAssistantPreparing_${patientId}`;
    const isProcessing = sessionStorage.getItem(isProcessingKey) === "true";

    if (isProcessing) {
      // Another instance is already processing, wait for it to complete
      aiLog(
        `⏳ Another instance is preparing sources for patient ${patientId}, waiting...`
      );
      setIsPreparingSources(true);

      // Poll for cache key to become available
      const pollInterval = setInterval(() => {
        const cacheKey = sessionStorage.getItem(
          `aiAssistantCacheKey_${patientId}`
        );
        if (cacheKey) {
          setCacheKey(cacheKey);
          setIsPreparingSources(false);
          clearInterval(pollInterval);
          aiLog(
            `✅ Cache ready for patient ${patientId} (prepared by another instance)`
          );
        }
      }, 500); // Check every 500ms

      // Cleanup after 60 seconds (timeout)
      const timeout = setTimeout(() => {
        clearInterval(pollInterval);
        setIsPreparingSources(false);
        console.warn("Timeout waiting for cache preparation");
      }, 60000);

      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
      };
    }

    // No processing in progress, start it ourselves
    let isMounted = true;
    const prepareSources = async () => {
      setIsPreparingSources(true);
      // Mark that we're processing (so other instances know to wait)
      sessionStorage.setItem(isProcessingKey, "true");

      try {
        const backendBaseUrl = (() => {
          const override = process.env.REACT_APP_BACKEND_URL;
          if (override && override.trim() !== "") return override;
          const hostname = window.location.hostname;
          return hostname === "localhost" || hostname === "127.0.0.1"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;
        })();

        const response = await fetch(
          `${backendBaseUrl}/api/ai-assistant/prepare-sources/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              patientId: patientId,
              patientData: patientData,
              documentContent: documentContent,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (isMounted) {
            setCacheKey(data.cacheKey);
          }
          // Store cache key in sessionStorage so it persists across component mounts
          // Always store it even if component unmounted, so other instances can use it
          sessionStorage.setItem(
            `aiAssistantCacheKey_${patientId}`,
            data.cacheKey
          );
          aiLog(
            `✅ Prepared and cached ${data.documentCount} documents for faster queries`
          );
        } else {
          console.warn(
            "Failed to prepare sources, will process on-demand:",
            await response.text()
          );
        }
      } catch (error) {
        console.error("Error preparing sources:", error);
        // Don't block the UI - queries will still work, just slower
      } finally {
        // Clear the processing flag so other instances know it's done
        sessionStorage.removeItem(isProcessingKey);
        if (isMounted) {
          setIsPreparingSources(false);
        }
      }
    };

    prepareSources();

    // Cleanup: if component unmounts during processing, clear the flag
    // (but only if we're the one who set it - check by seeing if it's still set)
    return () => {
      isMounted = false;
      // Only clear if we're the one processing (flag is still set)
      // This prevents clearing the flag if another instance took over
      if (sessionStorage.getItem(isProcessingKey) === "true") {
        // Check if cache key exists - if it does, processing completed successfully
        // If not, we might have been interrupted, but another instance might pick it up
        // For safety, we'll leave the flag - it will timeout or be cleared by the instance that completes
      }
    };
  }, [patientId, patientData, documentContent]);

  // Auto-remove unavailable sources from selectedSources
  // Update selectedSources when patientData changes (to include forms if they become available)
  // BUT: Don't auto-add sources if initialSelectedSources was provided (respect user's explicit choice)
  useEffect(() => {
    // If initialSelectedSources was provided, don't auto-modify selectedSources
    // This ensures that when Process audio/form buttons specify sources, they are respected
    // Check this FIRST before any other logic
    if (
      initialSelectedSources &&
      Array.isArray(initialSelectedSources) &&
      initialSelectedSources.length > 0
    ) {
      aiLog(
        "🔍 AIAssistant: Skipping auto-source update because initialSelectedSources was provided:",
        initialSelectedSources
      );
      // Also ensure selectedSources matches initialSelectedSources
      setSelectedSources((prev) => {
        const prevSet = new Set(prev);
        const initialSet = new Set(initialSelectedSources);
        if (
          prevSet.size !== initialSet.size ||
          ![...initialSet].every((s) => prevSet.has(s))
        ) {
          aiLog(
            "🔍 AIAssistant: Updating selectedSources to match initialSelectedSources"
          );
          return initialSelectedSources;
        }
        return prev;
      });
      return;
    }

    if (!patientData) return;

    // Handle both list and dict formats
    const patient =
      Array.isArray(patientData) && patientData.length > 0
        ? patientData[0]
        : typeof patientData === "object" && patientData !== null
        ? patientData
        : null;

    if (!patient) return;

    const hasForms =
      patient.submissions_array &&
      Array.isArray(patient.submissions_array) &&
      patient.submissions_array.length > 0;
    const hasDocument = documentContent && documentContent.trim().length > 0;

    setSelectedSources((prev) => {
      const updated = [...prev];

      // Remove forms if no forms data available (cleanup only - don't auto-add)
      if (!hasForms && updated.includes("forms")) {
        return updated.filter((s) => s !== "forms");
      }

      // Note: do NOT auto-add or auto-remove medical_document based on content availability.
      // The checkbox is already disabled when !documentContent, so it's safe to leave it
      // checked in selectedSources (the backend ignores it when content is empty).

      return updated;
    });
  }, [patientData, documentContent, initialSelectedSources]);

  const stopCurrentRequest = () => {
    const pendingQueryKey = `aiAssistantPendingQuery_${patientId}`;
    if (activeFetchController.current) {
      activeFetchController.current.abort();
      activeFetchController.current = null;
    }
    setIsLoading(false);
    try {
      sessionStorage.removeItem(pendingQueryKey);
    } catch (err) {
      console.warn("Failed to clear pending query on stop:", err);
    }
  };

  const handleSendMessage = async (overridePrompt = null) => {
    const isRetry = typeof overridePrompt === "string";
    const promptText = isRetry ? overridePrompt : inputMessage.trim();

    if (!promptText) return;

    // Guard: Require patientId before sending query
    // This prevents 400 errors when ChatBubble sends requests without valid patient context
    if (!patientId || !patientId.trim()) {
      console.warn("[AIAssistant] handleSendMessage: No patientId provided, skipping request");
      toast({
        title: "Cannot send message",
        description: "Please select a patient first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
      return;
    }

    // If there's an active request, abort it before starting a new one
    if (activeFetchController.current) {
      stopCurrentRequest();
    }

    const queryId = `query_${patientId}_${Date.now()}`;

    const userMessage = {
      id: Date.now().toString(),
      type: "user",
      content: promptText,
      timestamp: new Date(),
    };

    if (!isRetry) {
      setMessages((prev) => [...prev, userMessage]);
      setInputMessage("");
    }
    setIsLoading(true);

    // Store pending query info in sessionStorage so it persists across pop out/in
    const pendingQueryKey = `aiAssistantPendingQuery_${patientId}`;
    sessionStorage.setItem(
      pendingQueryKey,
      JSON.stringify({
        queryId: queryId,
        prompt: promptText,
        userMessageId: userMessage.id,
        timestamp: userMessage.timestamp.toISOString(),
      })
    );

    // Use ref to track mount status (will be updated by useEffect)
    const checkMounted = () => isMountedRef.current;

    try {
      // Get user ID, username, and JWT token from Supabase for auto-save feature
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const userJwtToken = session?.access_token || null; // Get JWT token for backend authentication

      // Get username from users table (same as in handleSubmitFeedback)
      let username = null;
      if (userId) {
        try {
          const { data: userData } = await supabase
            .from("users")
            .select("name, email")
            .eq("id", userId)
            .single();

          if (userData) {
            username = userData.name || userData.email || null;
          }
        } catch (err) {
          console.warn("Could not fetch username:", err);
          // Fallback to email from session
          username = session?.user?.email || null;
        }
      }

      // Extract patient name and scheduled date from patientData if available
      // Handle both array and object formats
      let patientName = null;
      let scheduledDate = null;
      if (patientData) {
        const patient =
          Array.isArray(patientData) && patientData.length > 0
            ? patientData[0]
            : !Array.isArray(patientData) && patientData
            ? patientData
            : null;
        if (patient) {
          patientName = patient["patient_name"] || patient["name"] || null;
          scheduledDate = patient["date_"] || null;
        }
      }

      const backendBaseUrl = (() => {
        const override = process.env.REACT_APP_BACKEND_URL;
        if (override && override.trim() !== "") return override;
        const hostname = window.location.hostname;
        return hostname === "localhost" || hostname === "127.0.0.1"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;
      })();

      // Get truncation parameters based on speed setting
      const truncationParams = getTruncationParameters();

      // Include Epic clinical documents if available for this patient
      let epicDocuments2 = null;
      try {
        const raw2 = sessionStorage.getItem(`epicDocs_${patientId}`);
        if (raw2) epicDocuments2 = JSON.parse(raw2);
      } catch (_) {}

      const controller = new AbortController();
      activeFetchController.current = controller;

      const response = await withAIQueryTracking('assistant', () => fetch(
        `${backendBaseUrl}/api/ai-assistant/query/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(userJwtToken ? { Authorization: `Bearer ${userJwtToken}` } : {}),
          },
          signal: controller.signal,
          body: JSON.stringify({
            query: promptText,
            patientId: patientId,
            patientData: getEffectivePatientData(patientData),
            documentContent: documentContent,
            sessionId: `session_${patientId}_${Date.now()}`,
            userId: userId, // Include userId for auto-save feature
            username: username, // Include username for auto-save feature
            patientName: patientName, // Include patient name for auto-save feature
            scheduledDate: scheduledDate, // Include scheduled date for auto-save feature
            selectedSources: selectedSources, // Include selected sources (used to filter cached documents)
            selectedReportFiles: selectedReportFiles.length < reportFiles.length ? selectedReportFiles : null, // Filter specific report files (null = all)
            cacheKey: cacheKey, // Include cache key for faster processing
            schema: supabaseSchema, // Include schema for correct table insertion
            userJwtToken: userJwtToken, // Include JWT token for authenticated Supabase requests
            userRole: userRole, // Include user role to avoid stale DB lookup
            threadId: activeThreadId, // Include thread ID for chat grouping
            maxCharsPerDocument: truncationParams.maxCharsPerDocument, // Include truncation parameters
            maxTotalContentLength: truncationParams.maxTotalContentLength, // Include truncation parameters
            modelId: selectedModels[0], // Backward compatibility
            modelIds: selectedModels, // New priority system (OCR)
            // Chat model routing: the "builtin Claude" path (chatModel set)
            // calls Anthropic directly with the Django service key, which is
            // subject to that account's usage cap. Tenant / freemium roles
            // (student, socialworker, other) must instead route through the
            // AgenticAI service — the same path the clinic-page assistant uses
            // for doctors & medstudents (which sends no chatModel). Sending
            // null here makes the backend take its `query_with_documents`
            // (agentic) branch. Clinical roles (doctor/admin) keep their
            // builtin model selection.
            chatModel: isInGroup(userRole, 'tenantSchema') ? null : chatModel,
            epicDocuments: epicDocuments2,
            activeSkill: activeSkill,
            skillIntensity: skillIntensity,
          }),
        }
      ));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      activeFetchController.current = null;

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        content:
          data.answer ||
          "I apologize, but I could not process your request at this time.",
        timestamp: new Date(),
        sources: data.sources || [],
        confidence: data.confidence || 0,
        prompt: promptText, // Store the prompt for feedback
        truncation_info: data.truncation_info || null, // Store truncation info if present
        uploaded_files_classification:
          data.uploaded_files_classification || null, // Store file classification if present
        latency_seconds: data.latency_seconds || null, // Store latency for display
        model_used: data.model_used || null, // Store model used
        activeSkill: data.activeSkill || null, // Skill actually applied to this reply
        skillIntensity: data.skillIntensity || null,
      };

      // Always store response in sessionStorage so it persists across component mounts
      const responseKey = `aiAssistantResponse_${queryId}`;
      sessionStorage.setItem(
        responseKey,
        JSON.stringify({
          queryId: queryId,
          assistantMessage: assistantMessage,
          timestamp: new Date().toISOString(),
        })
      );

      // Clear pending query flag
      sessionStorage.removeItem(pendingQueryKey);

      // Only update state if component is still mounted
      if (checkMounted()) {
        setMessages((prev) => [...prev, assistantMessage]);
        setIsLoading(false);

        // Remove this thread from empty threads in localStorage since it now has a message
        try {
          const storageKey = `ai_assistant_empty_threads_${patientId}`;
          const existingEmptyThreads = JSON.parse(
            localStorage.getItem(storageKey) || "[]"
          );
          const updatedEmptyThreads = existingEmptyThreads.filter(
            (t) => t.thread_id !== activeThreadId
          );
          localStorage.setItem(storageKey, JSON.stringify(updatedEmptyThreads));
        } catch (storageError) {
          console.warn(
            "Failed to update empty threads in localStorage:",
            storageError
          );
        }

        // Refresh threads to update last message time
        loadThreads();
      } else {
        // Component unmounted, but response is stored in sessionStorage
        // The component will pick it up when it remounts (handled in useEffect)
        aiLog(
          "Response received but component unmounted, stored in sessionStorage"
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        // Request was cancelled by user; do not show an error bubble
        activeFetchController.current = null;
        setIsLoading(false);
        return;
      }

      console.error("Error sending message to AI assistant:", error);
      activeFetchController.current = null;

      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        content:
          "I apologize, but I encountered an error processing your request. Please try again.",
        timestamp: new Date(),
        sources: [],
        isError: true,
      };

      // Store error response in sessionStorage as well
      const responseKey = `aiAssistantResponse_${queryId}`;
      sessionStorage.setItem(
        responseKey,
        JSON.stringify({
          queryId: queryId,
          assistantMessage: errorMessage,
          isError: true,
          timestamp: new Date().toISOString(),
        })
      );

      // Clear pending query flag
      sessionStorage.removeItem(pendingQueryKey);

      if (checkMounted()) {
        setMessages((prev) => [...prev, errorMessage]);
        setIsLoading(false);

        toast({
          title: "Error",
          description: "Failed to get response from AI assistant",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle sources checkbox group change - syncs Reports master with individual file selections
  const handleSourcesChange = (newSources) => {
    const wasReports = selectedSources.includes("uploaded_files");
    const isReports = newSources.includes("uploaded_files");
    if (!wasReports && isReports) {
      // Reports checked → select all individual files
      setSelectedReportFiles(reportFiles.map(f => f.name));
    } else if (wasReports && !isReports) {
      // Reports unchecked → deselect all individual files
      setSelectedReportFiles([]);
    }
    setSelectedSources(newSources);
  };

  const handleFeedbackClick = (messageId, status) => {
    setFeedbackStatus((prev) => ({ ...prev, [messageId]: status }));
    setShowFeedbackInput((prev) => ({ ...prev, [messageId]: true }));
  };

  const handleSubmitFeedback = async (messageId, prompt, response) => {
    if (submittingFeedback[messageId]) return;

    const status = feedbackStatus[messageId];
    if (!status) {
      toast({
        title: "Error",
        description: "Please select thumbs up or thumbs down first",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmittingFeedback((prev) => ({ ...prev, [messageId]: true }));

    try {
      // Get user ID, username, and JWT token from Supabase
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const userJwtToken = session?.access_token || null; // Get JWT token for backend authentication

      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Get username from users table
      let username = null;
      try {
        const { data: userData } = await supabase
          .from("users")
          .select("name, email")
          .eq("id", userId)
          .single();

        if (userData) {
          username = userData.name || userData.email || null;
        }
      } catch (err) {
        console.warn("Could not fetch username:", err);
        // Fallback to email from session
        username = session?.user?.email || null;
      }

      // Extract patient name and scheduled date from patientData if available
      // Handle both array and object formats
      let patientName = null;
      let scheduledDate = null;
      if (patientData) {
        const patient =
          Array.isArray(patientData) && patientData.length > 0
            ? patientData[0]
            : !Array.isArray(patientData) && patientData
            ? patientData
            : null;
        if (patient) {
          patientName = patient["patient_name"] || patient["name"] || null;
          scheduledDate = patient["date_"] || null;
        }
      }

      const backendBaseUrl = (() => {
        const override = process.env.REACT_APP_BACKEND_URL;
        if (override && override.trim() !== "") return override;
        const hostname = window.location.hostname;
        return hostname === "localhost" || hostname === "127.0.0.1"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;
      })();

      const response_fetch = await fetch(
        `${backendBaseUrl}/api/ai-assistant/feedback/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: userId,
            patientId: patientId,
            sessionId: `session_${patientId}_${Date.now()}`,
            prompt: prompt,
            response: response,
            feedbackText: feedbackText[messageId] || "",
            status: status,
            patientName: patientName, // Pass patient name if available
            username: username, // Pass username if available
            scheduledDate: scheduledDate, // Pass scheduled date if available
            schema: supabaseSchema, // Pass schema for correct table insertion
            userJwtToken: userJwtToken, // Pass JWT token for authenticated Supabase requests
          }),
        }
      );

      if (!response_fetch.ok) {
        // The backend puts the real cause (usually a Supabase/PostgREST
        // rejection) in `details`; without it every failure reads as a bare 500.
        let errBody = null;
        try { errBody = await response_fetch.json(); } catch (_) { /* non-JSON body */ }
        const detail = errBody && (errBody.details || errBody.error);
        throw new Error(
          detail
            ? `${detail} (${response_fetch.status})`
            : `HTTP error! status: ${response_fetch.status}`
        );
      }

      const data = await response_fetch.json();

      toast({
        title: "Thank you!",
        description: "Your feedback has been submitted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Hide feedback input after successful submission
      setShowFeedbackInput((prev) => ({ ...prev, [messageId]: false }));
      setFeedbackText((prev) => ({ ...prev, [messageId]: "" }));
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to submit feedback. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmittingFeedback((prev) => ({ ...prev, [messageId]: false }));
    }
  };

  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
      toast({
        title: "Copied",
        description: "Message copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle settings password verification
  const handleSettingsPasswordSubmit = () => {
    if (settingsPassword === SETTINGS_PASSWORD) {
      setIsSettingsUnlocked(true);
      setSettingsPasswordError("");
    } else {
      setSettingsPasswordError("Incorrect password");
      setSettingsPassword("");
    }
  };

  // Get truncation parameters based on speed setting
  const getTruncationParameters = () => {
    switch (speedSetting) {
      case "fast":
        return {
          maxCharsPerDocument: 5000,
          maxTotalContentLength: 50000,
        };
      case "medium":
        return {
          maxCharsPerDocument: 10000,
          maxTotalContentLength: 100000,
        };
      case "slow":
        return {
          maxCharsPerDocument: 1000000,
          maxTotalContentLength: 1000000,
        };
      case "advanced":
        return {
          maxCharsPerDocument: customMaxCharsPerDoc,
          maxTotalContentLength: customMaxTotalLength,
        };
      default:
        return {
          maxCharsPerDocument: 5000,
          maxTotalContentLength: 50000,
        };
    }
  };

  const renderMessage = (message) => {
    const isUser = message.type === "user";
    const isError = message.isError;

    return (
      <Box
        key={message.id}
        id={!isUser && !isError ? `ai-msg-${message.id}` : undefined}
        alignSelf={isUser ? "flex-end" : "flex-start"}
        maxW="85%"
        mb={4}
      >
        <VStack align={isUser ? "flex-end" : "flex-start"} spacing={2}>
          {/* Feedback section removed from here - moved outside chat window */}

          {/* File Classification - Show at top of assistant message, between query and response */}
          {!isUser &&
            !isError &&
            message.uploaded_files_classification &&
            Object.keys(message.uploaded_files_classification).length > 0 && (
              <Alert status="info" borderRadius="md" w="full" mb={2}>
                <AlertIcon />
                <VStack align="flex-start" spacing={1} flex={1}>
                  <AlertTitle fontSize="sm">
                    Uploaded Files Classification
                  </AlertTitle>
                  <AlertDescription fontSize="xs">
                    <VStack align="flex-start" spacing={1}>
                      {Object.entries(
                        message.uploaded_files_classification
                      ).map(([category, filenames]) => {
                        const shouldHighlight = isHighlightedCategory(category);
                        const categoryColor = shouldHighlight
                          ? getMetadataColor(category)
                          : "#000000"; // Black for non-highlighted categories

                        return (
                          <Box key={category}>
                            <Text
                              fontWeight="semibold"
                              mb={0.5}
                              color={categoryColor}
                              style={{
                                backgroundColor: shouldHighlight
                                  ? getMetadataBgColor(category)
                                  : "transparent",
                                padding: shouldHighlight ? "2px 6px" : "0",
                                borderRadius: shouldHighlight ? "4px" : "0",
                                display: "inline-block",
                              }}
                            >
                              {category}:
                            </Text>
                            <VStack align="flex-start" spacing={0.5} pl={2}>
                              {filenames.map((filename, idx) => (
                                <Text key={idx} color={categoryColor}>
                                  • {filename}
                                </Text>
                              ))}
                            </VStack>
                          </Box>
                        );
                      })}
                    </VStack>
                  </AlertDescription>
                </VStack>
              </Alert>
            )}

          {/* Truncation Notice - Show at top of assistant message, between query and response */}
          {!isUser && !isError && message.truncation_info && (
            <Alert
              status={
                (message.truncation_info.truncated_count || 0) > 0
                  ? "warning"
                  : "info"
              }
              borderRadius="md"
              w="full"
            >
              <AlertIcon />
              <VStack align="flex-start" spacing={1} flex={1}>
                <AlertTitle fontSize="sm">
                  {(message.truncation_info.truncated_count || 0) > 0
                    ? "Some documents were truncated"
                    : "No truncation applied"}
                </AlertTitle>
                <AlertDescription fontSize="xs">
                  {message.truncation_info.messages &&
                  message.truncation_info.messages.length > 0 ? (
                    <VStack align="flex-start" spacing={0.5}>
                      {message.truncation_info.messages.map((msg, idx) => (
                        <Text key={idx}>• {msg}</Text>
                      ))}
                    </VStack>
                  ) : (
                    <Text>
                      Per-doc limit{" "}
                      {message.truncation_info.max_chars_per_document?.toLocaleString() ||
                        "N/A"}
                      , total limit{" "}
                      {message.truncation_info.max_total_content_length?.toLocaleString() ||
                        "N/A"}
                      ; content{" "}
                      {message.truncation_info.total_content_length?.toLocaleString() ||
                        "N/A"}
                    </Text>
                  )}
                </AlertDescription>
              </VStack>
            </Alert>
          )}

          {/* Sources - moved above message content */}
          {!isUser &&
            !isError &&
            message.sources &&
            message.sources.length > 0 && (
              <VStack
                id={`ai-msg-sources-${message.id}`}
                align="flex-start"
                spacing={1}
                w="full"
              >
                <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                  Sources:
                </Text>
                {message.sources.map((source, index) => (
                  <VStack key={index} align="flex-start" spacing={1} w="full">
                    <HStack spacing={2}>
                      <Badge colorScheme="blue" size="sm">
                        {source.type || "Document"}
                      </Badge>
                      <Text fontSize="xs" color="gray.600">
                        {source.title ||
                          source.filename ||
                          `Source ${index + 1}`}
                      </Text>
                      {source.url && (
                        <Link href={source.url} isExternal>
                          <ExternalLinkIcon boxSize={3} />
                        </Link>
                      )}
                    </HStack>
                    {source.metadata &&
                      typeof source.metadata === "object" &&
                      Object.keys(source.metadata).length > 0 && (
                        <Box fontSize="xs" color="gray.600" pl={4} pt={1}>
                          {source.metadata.patient_name && (
                            <Text>
                              <strong>Patient:</strong>{" "}
                              {source.metadata.patient_name}
                            </Text>
                          )}
                          {source.metadata.date_of_service && (
                            <Text>
                              <strong>Date:</strong>{" "}
                              {source.metadata.date_of_service}
                            </Text>
                          )}
                          {source.metadata.test_name && (
                            <Text>
                              <strong>Test:</strong> {source.metadata.test_name}
                            </Text>
                          )}
                          {source.metadata.facility && (
                            <Text>
                              <strong>Facility:</strong>{" "}
                              {source.metadata.facility}
                            </Text>
                          )}
                          {source.metadata.report_type && (
                            <Text>
                              <strong>Type:</strong>{" "}
                              {source.metadata.report_type}
                            </Text>
                          )}
                        </Box>
                      )}
                  </VStack>
                ))}
              </VStack>
            )}

          <Box
            id={!isUser && !isError ? `ai-msg-${message.id}` : undefined}
            bg={isUser ? "blue.500" : isError ? "red.100" : "gray.100"}
            color={isUser ? "white" : isError ? "red.800" : "gray.800"}
            px={4}
            py={3}
            borderRadius="lg"
            position="relative"
            _hover={{
              "& .copy-button": {
                opacity: 1,
              },
            }}
          >
            {isUser ? (
              <Text whiteSpace="pre-wrap" fontSize="sm">
                {message.content}
              </Text>
            ) : (
              <Box
                fontSize="sm"
                sx={{
                  h1: {
                    fontSize: "lg",
                    fontWeight: "bold",
                    marginBottom: "8px",
                  },
                  h2: {
                    fontSize: "md",
                    fontWeight: "semibold",
                    marginTop: "8px",
                    marginBottom: "6px",
                  },
                  h3: {
                    fontSize: "sm",
                    fontWeight: "semibold",
                    marginTop: "6px",
                    marginBottom: "4px",
                  },
                  ul: {
                    paddingInlineStart: "20px",
                    marginTop: "4px",
                    marginBottom: "4px",
                  },
                  ol: {
                    paddingInlineStart: "20px",
                    marginTop: "4px",
                    marginBottom: "4px",
                  },
                  li: { marginTop: "2px", marginBottom: "2px" },
                  p: { marginBottom: "6px" },
                  strong: { fontWeight: 700 },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </ReactMarkdown>
              </Box>
            )}

            {/* Copy button */}
            <IconButton
              className="copy-button"
              size="xs"
              icon={
                copiedMessageId === message.id ? <CheckIcon /> : <CopyIcon />
              }
              onClick={() => copyToClipboard(message.content, message.id)}
              position="absolute"
              top={1}
              right={1}
              opacity={0}
              transition="opacity 0.2s"
              aria-label="Copy message"
            />
          </Box>

          {/* Confidence score */}
          {message.confidence && (
            <HStack spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Confidence:
              </Text>
              <Badge
                colorScheme={
                  message.confidence > 0.8
                    ? "green"
                    : message.confidence > 0.6
                    ? "yellow"
                    : "red"
                }
                size="sm"
              >
                {Math.round(message.confidence * 100)}%
              </Badge>
            </HStack>
          )}

          {/* Latency display */}
          {!isUser && message.latency_seconds && (
            <HStack spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Response time:
              </Text>
              <Badge colorScheme="blue" size="sm">
                {message.latency_seconds}s
              </Badge>
            </HStack>
          )}

          {/* Model display */}
          {!isUser && message.model_used && (
            <HStack spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Model:
              </Text>
              <Badge colorScheme="purple" size="sm">
                {message.model_used}
              </Badge>
            </HStack>
          )}

          {/* Skill display — shows which skill (if any) actually shaped this reply */}
          {!isUser && (
            <HStack spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Skill:
              </Text>
              <Badge colorScheme={message.activeSkill ? "orange" : "gray"} size="sm">
                {message.activeSkill
                  ? `Caveman (${message.skillIntensity || "full"})`
                  : "None"}
              </Badge>
            </HStack>
          )}

          <Text fontSize="xs" color="gray.400">
            {formatTimestamp(message.timestamp)}
          </Text>
        </VStack>
      </Box>
    );
  };

  // Custom CSS for resize handles
  const resizeHandleStyle = `
    .react-resizable-handle {
      background: #007bff !important;
      border: 2px solid #fff !important;
      border-radius: 3px !important;
      width: 12px !important;
      height: 12px !important;
      opacity: 0.8 !important;
    }
    .react-resizable-handle:hover {
      opacity: 1 !important;
      background: #0056b3 !important;
    }
  `;

  // If this is a global popup, only render the popup, not the inline component
  if (isGlobalPopup) {
    return (
      <Portal>
        <style>{resizeHandleStyle}</style>
        <Box
          ref={popupRef}
          position="fixed"
          left={`${position.x}px`}
          top={`${position.y}px`}
          zIndex={9999}
        >
          <ResizableBox
            width={popupSize.width}
            height={isPopupMinimized ? 60 : popupSize.height}
            minConstraints={[400, isPopupMinimized ? 60 : 400]}
            maxConstraints={[Infinity, Infinity]}
            resizeHandles={isPopupMinimized ? [] : ["se", "sw", "ne", "nw"]}
            onResizeStart={handleResizeStart}
            onResize={handleResize}
            onResizeStop={handleResizeStop}
            style={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              border: "1px solid",
              borderColor: "#cbd5e0",
              cursor: isDragging ? "grabbing" : "default",
            }}
          >
            <Box
              w="100%"
              h="100%"
              display="flex"
              flexDirection="column"
              overflow="hidden"
            >
              {/* Draggable Header */}
              <Flex
                align="center"
                justify="space-between"
                p={4}
                bg="blue.50"
                borderBottom="1px solid"
                borderColor="gray.200"
                cursor={isDragging ? "grabbing" : "grab"}
                _active={{ cursor: "grabbing" }}
                onMouseDown={handleHeaderMouseDown}
                userSelect="none"
              >
                <HStack spacing={3} flex="1">
                  <ChatIcon color="blue.500" />
                  <Heading size="md" color="blue.700">
                    AI Assistant
                  </Heading>
                  {isGlobalPopup &&
                    patientData &&
                    (Array.isArray(patientData)
                      ? patientData[0]
                      : patientData)?.[prompts[0][0]] && (
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        {
                          (Array.isArray(patientData)
                            ? patientData[0]
                            : patientData)[prompts[0][0]]
                        }
                      </Text>
                    )}
                  <Text fontSize="md" color="blue.600" fontWeight="medium">
                    Chat {activeThreadId}
                  </Text>
                  {messages.length > 1 && (
                    <Badge colorScheme="blue" size="sm">
                      {messages.filter((m) => m.type === "user").length} queries
                    </Badge>
                  )}
                  {renderSkillHeaderBadge()}
                </HStack>
                <HStack spacing={1}>
                  <IconButton
                    size="sm"
                    icon={<SettingsIcon />}
                    variant="ghost"
                    aria-label="Settings"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSettingsOpen(true);
                      setIsSettingsUnlocked(false);
                      setSettingsPassword("");
                    }}
                    colorScheme="blue"
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Settings"
                  />
                  <IconButton
                    size="sm"
                    icon={<ArrowUpIcon />}
                    variant="ghost"
                    aria-label="Move to side"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveToSide();
                    }}
                    colorScheme="blue"
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Move to side"
                  />
                  <IconButton
                    size="sm"
                    icon={
                      isPopupMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />
                    }
                    variant="ghost"
                    aria-label={isPopupMinimized ? "Maximize" : "Minimize"}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePopupMinimize();
                    }}
                    colorScheme="blue"
                    onMouseDown={(e) => e.stopPropagation()}
                    title={isPopupMinimized ? "Maximize" : "Minimize"}
                  />
                  <IconButton
                    size="sm"
                    icon={<CloseIcon />}
                    variant="ghost"
                    aria-label="Close"
                    onClick={handleClose}
                    colorScheme="blue"
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </HStack>
              </Flex>

              {/* Chat Interface - Only show when not minimized */}
              {!isPopupMinimized && (
                <VStack spacing={0} h="100%" flex="1" overflow="hidden">
                  {/* Chat Tabs */}
                  <Box
                    w="full"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    bg="gray.50"
                    px={2}
                    py={1}
                    onMouseDown={(e) => e.stopPropagation()}
                    flexShrink={0}
                    flexGrow={0}
                    minH="40px"
                    maxH="40px"
                    overflow="hidden"
                  >
                    <HStack
                      spacing={1}
                      overflowX="auto"
                      overflowY="hidden"
                      w="full"
                      maxW="100%"
                    >
                      <HStack
                        spacing={1}
                        overflowX="auto"
                        overflowY="hidden"
                        w="90%"
                        maxW="90%"
                        flexShrink={1}
                      >
                        {threads
                          .filter((thread) => {
                            // During shifting phase, keep closed tab in DOM (collapsed). After resizing, hide it.
                            if (
                              tabAnimationPhase === "shifting" &&
                              recentlyClosedThreads.has(thread.thread_id)
                            ) {
                              return true;
                            }
                            return !closedThreads.has(thread.thread_id);
                          })
                          .map((thread, index) => {
                            const isClosed =
                              recentlyClosedThreads.has(thread.thread_id) &&
                              tabAnimationPhase === "shifting";

                            // Calculate how many closed tabs are before this one in the original threads array
                            const threadIndex = threads.findIndex(
                              (t) => t.thread_id === thread.thread_id
                            );
                            const closedBeforeCount = threads
                              .slice(0, threadIndex)
                              .filter(
                                (t) =>
                                  recentlyClosedThreads.has(t.thread_id) &&
                                  tabAnimationPhase === "shifting"
                              ).length;

                            // Find the previous open (non-closed) tab in the original threads array
                            let previousOpenTab = null;
                            for (let i = threadIndex - 1; i >= 0; i--) {
                              const prevThread = threads[i];
                              if (
                                !recentlyClosedThreads.has(
                                  prevThread.thread_id
                                ) ||
                                tabAnimationPhase !== "shifting"
                              ) {
                                previousOpenTab = prevThread;
                                break;
                              }
                            }

                            // Count consecutive closed tabs immediately before this one
                            let consecutiveClosedCount = 0;
                            for (let i = threadIndex - 1; i >= 0; i--) {
                              if (
                                recentlyClosedThreads.has(
                                  threads[i].thread_id
                                ) &&
                                tabAnimationPhase === "shifting"
                              ) {
                                consecutiveClosedCount++;
                              } else {
                                break;
                              }
                            }

                            // Calculate width: during shifting, closed tab has width 0, others keep original.
                            // During resizing, distribute evenly among remaining tabs, but allow expansion up to 90% of bar.
                            let tabWidth;
                            let maxTabWidth = "90%";
                            let transformX = "0px";

                            if (isClosed) {
                              // Closed tab: collapse to width 0 during shifting phase
                              tabWidth = "0px";
                              maxTabWidth = "0px";
                            } else if (tabAnimationPhase === "shifting") {
                              // During shifting, other tabs keep their original width
                              const allVisibleThreads = threads.filter(
                                (t) =>
                                  !closedThreads.has(t.thread_id) ||
                                  (recentlyClosedThreads.has(t.thread_id) &&
                                    tabAnimationPhase === "shifting")
                              );
                              const closedCount = Array.from(
                                allVisibleThreads
                              ).filter((t) =>
                                recentlyClosedThreads.has(t.thread_id)
                              ).length;
                              const openCount =
                                allVisibleThreads.length - closedCount;
                              tabWidth =
                                openCount > 0
                                  ? `${90 / allVisibleThreads.length}%`
                                  : "auto";

                              // Calculate transform to shift left by the width of closed tabs before this one
                              // Only shift by consecutive closed tabs immediately before to prevent overlap
                              if (consecutiveClosedCount > 0) {
                                const tabWidthPercent =
                                  90 / allVisibleThreads.length;
                                // Shift by consecutive closed tabs only, ensuring we don't overlap with previous open tab
                                transformX = `calc(-1 * ${tabWidthPercent}% * ${consecutiveClosedCount})`;
                              }
                            } else {
                              // Resize to fill space after shifting
                              const openThreads = threads.filter(
                                (t) => !closedThreads.has(t.thread_id)
                              );
                              tabWidth =
                                openThreads.length > 0
                                  ? `${90 / openThreads.length}%`
                                  : "auto";
                            }

                            return (
                              <Button
                                key={thread.thread_id}
                                size="sm"
                                variant={
                                  activeThreadId === thread.thread_id
                                    ? "solid"
                                    : "ghost"
                                }
                                colorScheme={
                                  activeThreadId === thread.thread_id
                                    ? "blue"
                                    : "gray"
                                }
                                onClick={() => switchThread(thread.thread_id)}
                                minW={tabWidth}
                                maxW={maxTabWidth}
                                w={tabWidth}
                                px={isClosed ? 0 : 2}
                                pr={isClosed ? 0 : 1}
                                flexShrink={0}
                                flexGrow={0}
                                opacity={isClosed ? 0 : 1}
                                overflow="hidden"
                                transform={
                                  transformX !== "0px"
                                    ? `translateX(${transformX})`
                                    : "none"
                                }
                                transition={
                                  tabAnimationPhase === "shifting"
                                    ? "min-width 0.3s ease-out, max-width 0.3s ease-out, opacity 0.3s ease-out, padding 0.3s ease-out, transform 0.3s ease-out"
                                    : tabAnimationPhase === "resizing"
                                    ? "min-width 0.3s ease-out, max-width 0.3s ease-out, transform 0.3s ease-out"
                                    : "none"
                                }
                                pointerEvents={isClosed ? "none" : "auto"}
                              >
                                <HStack
                                  spacing={1}
                                  w="full"
                                  justify="space-between"
                                >
                                  <Text fontSize="sm" isTruncated>
                                    Chat {thread.thread_id}
                                  </Text>
                                  <Box
                                    as="button"
                                    aria-label="Close thread"
                                    onClick={(e) =>
                                      closeThread(thread.thread_id, e)
                                    }
                                    fontSize="xs"
                                    lineHeight="1"
                                    p={0}
                                    m={0}
                                    minW="12px"
                                    h="12px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    _hover={{ opacity: 0.8 }}
                                    flexShrink={0}
                                  >
                                    <CloseIcon boxSize="8px" />
                                  </Box>
                                </HStack>
                              </Button>
                            );
                          })}
                        {!isLoadingThreads && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={createNewThread}
                            minW="auto"
                            px={2}
                            title="New Chat"
                            flexShrink={0}
                          >
                            +
                          </Button>
                        )}
                      </HStack>
                      {threads.length > 0 && (
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            aria-label="View all threads"
                            icon={<HamburgerIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="gray"
                            flexShrink={0}
                            alignSelf="flex-start"
                            mt="10px"
                          />
                          <MenuList maxH="300px" overflowY="auto">
                            {threads.map((thread) => {
                              const isClosed = closedThreads.has(
                                thread.thread_id
                              );
                              return (
                                <MenuItem
                                  key={thread.thread_id}
                                  onClick={() => switchThread(thread.thread_id)}
                                  bg={
                                    activeThreadId === thread.thread_id
                                      ? "blue.50"
                                      : "transparent"
                                  }
                                >
                                  <HStack w="full" justify="space-between">
                                    <Text>
                                      Chat {thread.thread_id}
                                      {isClosed && (
                                        <Text
                                          as="span"
                                          fontSize="xs"
                                          color="gray.500"
                                          ml={2}
                                        >
                                          (closed)
                                        </Text>
                                      )}
                                    </Text>
                                  </HStack>
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </Menu>
                      )}
                    </HStack>
                  </Box>

                  {/* Messages */}
                  <Box
                    flex="1"
                    overflowY="auto"
                    p={4}
                    w="full"
                    ref={modalMessagesContainerRef}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <VStack spacing={0} align="stretch">
                      {messages.map(renderMessage)}
                      {isLoading && (
                        <Box alignSelf="flex-start" mb={4}>
                          <HStack spacing={2}>
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="sm" color="gray.500">
                              AI is thinking...
                            </Text>
                          </HStack>
                        </Box>
                      )}
                      <div ref={messagesEndRef} />
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Feedback Section - Outside chat window, above input */}
                  {(() => {
                    // Find the last assistant message (error or not)
                    const lastAssistantMessage = [...messages]
                      .reverse()
                      .find(
                        (m) => m.type === "assistant" && m.id !== "welcome"
                      );

                    if (!lastAssistantMessage) return null;

                    const canShowFeedback =
                      lastAssistantMessage.prompt &&
                      !lastAssistantMessage.isError;
                    const lastUserMessage = [...messages]
                      .reverse()
                      .find((m) => m.type === "user");

                    return (
                      <Box
                        p={3}
                        w="full"
                        borderTop="1px solid"
                        borderColor="gray.200"
                        bg={lastAssistantMessage.isError ? "red.50" : "blue.50"}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <HStack
                          justify="space-between"
                          align="center"
                          mb={showFeedback && canShowFeedback ? 2 : 0}
                        >
                          {canShowFeedback ? (
                            <Text
                              fontSize="sm"
                              color="gray.700"
                              fontWeight="semibold"
                            >
                              💬 Was this response helpful?
                            </Text>
                          ) : (
                            <Text
                              fontSize="sm"
                              color={
                                lastAssistantMessage.isError
                                  ? "red.600"
                                  : "gray.600"
                              }
                              fontWeight="semibold"
                            >
                              {lastAssistantMessage.isError
                                ? "⚠️ Response generation failed"
                                : "Response generated"}
                            </Text>
                          )}

                          <HStack spacing={2}>
                            {lastUserMessage && (
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme={
                                  lastAssistantMessage.isError ? "red" : "blue"
                                }
                                leftIcon={<RepeatIcon />}
                                onClick={() =>
                                  handleSendMessage(lastUserMessage.content)
                                }
                                isDisabled={isLoading}
                                bg="white"
                              >
                                Try Again
                              </Button>
                            )}

                            {canShowFeedback && (
                              <IconButton
                                size="xs"
                                icon={
                                  showFeedback ? (
                                    <ChevronUpIcon />
                                  ) : (
                                    <ChevronDownIcon />
                                  )
                                }
                                variant="ghost"
                                aria-label={
                                  showFeedback
                                    ? "Hide feedback"
                                    : "Show feedback"
                                }
                                onClick={() => setShowFeedback(!showFeedback)}
                                colorScheme="gray"
                              />
                            )}
                          </HStack>
                        </HStack>

                        {canShowFeedback && (
                          <Collapse in={showFeedback}>
                            <VStack align="flex-start" spacing={3} w="full">
                              <HStack spacing={3} w="full">
                                <Button
                                  size="sm"
                                  variant={
                                    feedbackStatus[lastAssistantMessage.id] ===
                                    "positive"
                                      ? "solid"
                                      : "outline"
                                  }
                                  colorScheme={
                                    feedbackStatus[lastAssistantMessage.id] ===
                                    "positive"
                                      ? "green"
                                      : "gray"
                                  }
                                  onClick={() =>
                                    handleFeedbackClick(
                                      lastAssistantMessage.id,
                                      "positive"
                                    )
                                  }
                                  isDisabled={
                                    submittingFeedback[lastAssistantMessage.id]
                                  }
                                  leftIcon={<span>👍</span>}
                                >
                                  Helpful
                                </Button>
                                <Button
                                  size="sm"
                                  variant={
                                    feedbackStatus[lastAssistantMessage.id] ===
                                    "negative"
                                      ? "solid"
                                      : "outline"
                                  }
                                  colorScheme={
                                    feedbackStatus[lastAssistantMessage.id] ===
                                    "negative"
                                      ? "red"
                                      : "gray"
                                  }
                                  onClick={() =>
                                    handleFeedbackClick(
                                      lastAssistantMessage.id,
                                      "negative"
                                    )
                                  }
                                  isDisabled={
                                    submittingFeedback[lastAssistantMessage.id]
                                  }
                                  leftIcon={<span>👎</span>}
                                >
                                  Not Helpful
                                </Button>
                              </HStack>

                              {/* Feedback text input - shown when thumbs up/down is clicked */}
                              {showFeedbackInput[lastAssistantMessage.id] && (
                                <VStack
                                  align="flex-start"
                                  spacing={2}
                                  w="full"
                                  mt={2}
                                >
                                  <Textarea
                                    size="sm"
                                    placeholder="Optional: Tell us more about your feedback..."
                                    value={
                                      feedbackText[lastAssistantMessage.id] ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      setFeedbackText((prev) => ({
                                        ...prev,
                                        [lastAssistantMessage.id]:
                                          e.target.value,
                                      }))
                                    }
                                    rows={2}
                                    resize="none"
                                    isDisabled={
                                      submittingFeedback[
                                        lastAssistantMessage.id
                                      ]
                                    }
                                  />
                                  <HStack spacing={2}>
                                    <Button
                                      size="sm"
                                      colorScheme="blue"
                                      onClick={() => {
                                        const prompt =
                                          lastAssistantMessage.prompt || "";
                                        handleSubmitFeedback(
                                          lastAssistantMessage.id,
                                          prompt,
                                          lastAssistantMessage.content
                                        );
                                      }}
                                      isLoading={
                                        submittingFeedback[
                                          lastAssistantMessage.id
                                        ]
                                      }
                                      loadingText="Submitting..."
                                    >
                                      Submit Feedback
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setShowFeedbackInput((prev) => ({
                                          ...prev,
                                          [lastAssistantMessage.id]: false,
                                        }));
                                        setFeedbackStatus((prev) => {
                                          const newStatus = { ...prev };
                                          delete newStatus[
                                            lastAssistantMessage.id
                                          ];
                                          return newStatus;
                                        });
                                        setFeedbackText((prev) => {
                                          const newText = { ...prev };
                                          delete newText[
                                            lastAssistantMessage.id
                                          ];
                                          return newText;
                                        });
                                      }}
                                      isDisabled={
                                        submittingFeedback[
                                          lastAssistantMessage.id
                                        ]
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  </HStack>
                                </VStack>
                              )}
                            </VStack>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })()}

                  {/* Source Selection */}
                  <Box
                    p={3}
                    w="full"
                    borderTop="1px solid"
                    borderColor="gray.200"
                    bg="gray.50"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <HStack
                      justify="space-between"
                      align="center"
                      mb={showSources ? 2 : 0}
                    >
                      <Text
                        fontSize="xs"
                        color="gray.600"
                        fontWeight="semibold"
                      >
                        Include Sources:
                      </Text>
                      <IconButton
                        size="xs"
                        icon={
                          showSources ? <ChevronUpIcon /> : <ChevronDownIcon />
                        }
                        variant="ghost"
                        aria-label={
                          showSources ? "Hide sources" : "Show sources"
                        }
                        onClick={() => setShowSources(!showSources)}
                        colorScheme="gray"
                      />
                    </HStack>
                    <Collapse in={showSources}>
                      <CheckboxGroup
                        value={selectedSources}
                        onChange={handleSourcesChange}
                        size="sm"
                      >
                        <HStack spacing={4} flexWrap="wrap" align="flex-start">
                          {!isMagicNotes && <Checkbox value={entityInfoSource} isDisabled={isLoading}>
                            {getLabel(userRole, 'entity')} Info
                          </Checkbox>}
                          <Checkbox
                            value={documentSource}
                            isDisabled={isLoading || !documentContent}
                            flexShrink={0}
                          >
                            {isMagicNotes ? "Magic Notes" : getLabel(userRole, 'documentName')}
                          </Checkbox>
                          {!isMagicNotes && <VStack spacing={1} align="flex-start" flexShrink={0} w="max-content" minW={0} alignSelf="flex-start">
                            <HStack spacing={0} align="center">
                              <Checkbox
                                value="uploaded_files"
                                isDisabled={isLoading || !hasReports}
                              >
                                Reports
                              </Checkbox>
                              {hasReports && reportFiles.length > 1 && (
                                <IconButton
                                  size="sm"
                                  icon={showReportDropdown ? <ChevronDownIcon boxSize={5} /> : <ChevronRightIcon boxSize={5} />}
                                  variant="ghost"
                                  aria-label="Select specific reports"
                                  onClick={(e) => { e.stopPropagation(); aiLog("📂 Reports arrow clicked:", { patientId, effectiveDate, reportFiles, selectedReportFiles, rawPath: (Array.isArray(patientData) ? patientData[0] : patientData)?.reports }); setShowReportDropdown(d => !d); }}
                                  colorScheme="gray"
                                  fontWeight="bold"
                                  minW={8}
                                  minH={8}
                                />
                              )}
                            </HStack>
                            {showReportDropdown && reportFiles.length > 1 && (
                              <Box pl={2} borderLeft="2px solid" borderColor="blue.200">
                                <VStack align="flex-start" spacing={0.5}>
                                  {reportFiles.map(file => (
                                    <Checkbox
                                      key={file.name}
                                      size="sm"
                                      isChecked={selectedReportFiles.includes(file.name)}
                                      onChange={(e) => {
                                        const newFiles = e.target.checked
                                          ? [...selectedReportFiles, file.name]
                                          : selectedReportFiles.filter(n => n !== file.name);
                                        setSelectedReportFiles(newFiles);
                                        if (newFiles.length === 0) {
                                          setSelectedSources(s => s.filter(s => s !== "uploaded_files"));
                                        } else if (e.target.checked && !selectedSources.includes("uploaded_files")) {
                                          setSelectedSources(s => [...s, "uploaded_files"]);
                                        }
                                      }}
                                      isDisabled={isLoading}
                                    >
                                      <Text fontSize="xs">{file.name}</Text>
                                    </Checkbox>
                                  ))}
                                </VStack>
                              </Box>
                            )}
                          </VStack>}
                          {!isMagicNotes && <Checkbox
                            value="visit_history"
                            isDisabled={isLoading}
                            flexShrink={0}
                          >
                            {historyLabel}
                          </Checkbox>}
                          {!isMagicNotes && canAccessComponent(userRole, 'aiAssistant.formsSource') && (
                            <Checkbox
                              value="forms"
                              isDisabled={isLoading || !hasFormsData()}
                            >
                              Forms
                            </Checkbox>
                          )}
                          {!isMagicNotes && <Checkbox
                            value="audio_transcripts"
                            isDisabled={isLoading}
                            flexShrink={0}
                          >
                            Audio/Transcripts
                          </Checkbox>}
                          <Checkbox value="ai_knowledge" isDisabled={isLoading} flexShrink={0}>
                            AI Knowledge
                          </Checkbox>
                        </HStack>
                      </CheckboxGroup>
                    </Collapse>
                  </Box>

                  {/* Input */}
                  <Box
                    p={4}
                    w="full"
                    borderTop="1px solid"
                    borderColor="gray.200"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <HStack spacing={2}>
                      <Textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask me anything about this patient's medical records..."
                        size="sm"
                        resize="none"
                        rows={2}
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      />
                      {isLoading ? (
                        <Button
                          colorScheme="red"
                          onClick={stopCurrentRequest}
                          size="sm"
                          minW="40px"
                          height="40px"
                          px={0}
                          borderRadius="md"
                          title="Stop"
                        >
                          ■
                        </Button>
                      ) : (
                        <Button
                          colorScheme="blue"
                          onClick={handleSendMessage}
                          isDisabled={!inputMessage.trim()}
                          size="sm"
                          px={6}
                        >
                          Send
                        </Button>
                      )}
                    </HStack>
                  </Box>
                </VStack>
              )}
            </Box>
          </ResizableBox>
        </Box>

        {/* Settings Modal */}
        <Modal
          isOpen={isSettingsOpen}
          onClose={() => {
            setIsSettingsOpen(false);
            setIsSettingsUnlocked(false);
            setSettingsPassword("");
            setSettingsPasswordError("");
          }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>AI Assistant Settings</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {!isSettingsUnlocked ? (
                <VStack spacing={4} align="stretch">
                  <Text fontSize="sm" color="gray.600">
                    Enter password to access settings:
                  </Text>
                  <FormControl isInvalid={!!settingsPasswordError}>
                    <Input
                      type="password"
                      value={settingsPassword}
                      onChange={(e) => {
                        setSettingsPassword(e.target.value);
                        setSettingsPasswordError("");
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSettingsPasswordSubmit();
                        }
                      }}
                      placeholder="Password"
                    />
                    {settingsPasswordError && (
                      <FormErrorMessage>
                        {settingsPasswordError}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                  <Button
                    colorScheme="blue"
                    onClick={handleSettingsPasswordSubmit}
                  >
                    Unlock Settings
                  </Button>
                </VStack>
              ) : (
                <VStack spacing={4} align="stretch">
                  {renderSettingsBody()}
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Portal>
    );
  }

  return (
    <Box
      bg="white"
      borderRadius="md"
      boxShadow="md"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      flexShrink={0}
      flexGrow={fullHeight ? 1 : 0}
      w="100%"
      h={fullHeight ? "100%" : "auto"}
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Flex
        align="center"
        justify="space-between"
        py={3}
        px={4}
        bg="blue.50"
        borderBottom="1px solid"
        borderColor="gray.200"
        minH="64px"
        maxH="64px"
        w="100%"
        flexShrink={0}
        sx={{
          justifyContent: "space-between !important",
          alignItems: "center !important",
        }}
      >
        <HStack
          spacing={2}
          cursor="pointer"
          onClick={() => setIsExpanded(!isExpanded)}
          _hover={{ opacity: 0.8 }}
          flexShrink={0}
          flexGrow={0}
        >
          <ChatIcon color="blue.500" boxSize={4} />
          <Heading size="sm" color="blue.700">
            AI Assistant
          </Heading>
          {isGlobalPopup &&
            patientData &&
            (Array.isArray(patientData) ? patientData[0] : patientData)?.[
              prompts[0][0]
            ] && (
              <Text fontSize="xs" color="gray.600" fontWeight="medium">
                {
                  (Array.isArray(patientData) ? patientData[0] : patientData)[
                    prompts[0][0]
                  ]
                }
              </Text>
            )}
          <Text fontSize="sm" color="blue.600" fontWeight="medium">
            Chat {activeThreadId}
          </Text>
          {messages.length > 1 && (
            <Badge colorScheme="blue" fontSize="xs">
              {messages.filter((m) => m.type === "user").length} QUERIES
            </Badge>
          )}
          <Badge colorScheme="purple" fontSize="xs" ml={1}>
            {speedSetting === "advanced"
              ? "Custom"
              : speedSetting.charAt(0).toUpperCase() + speedSetting.slice(1)}
          </Badge>
          {renderSkillHeaderBadge()}
        </HStack>

        <HStack spacing={1} flexShrink={0} flexGrow={0}>
          <IconButton
            size="sm"
            icon={<SettingsIcon />}
            variant="ghost"
            aria-label="Settings"
            onClick={(e) => {
              e.stopPropagation();
              setIsSettingsOpen(true);
              setIsSettingsUnlocked(false);
              setSettingsPassword("");
            }}
            colorScheme="blue"
            title="Settings"
          />
          <IconButton
            size="sm"
            icon={<ArrowUpIcon />}
            variant="ghost"
            aria-label="Pop out"
            onClick={(e) => {
              e.stopPropagation();
              if (onPopOut) {
                // Use global popup callback if provided - pass messages, selectedSources, activeThreadId, and threads to preserve state
                onPopOut(
                  patientId,
                  patientData,
                  documentContent,
                  messages,
                  selectedSources,
                  activeThreadId,
                  threads,
                  effectiveDate
                );
              } else {
                // Fallback to local popup
                onPopOutOpen();
              }
            }}
            colorScheme="blue"
            title="Pop out AI Assistant in a separate window"
          />
          <IconButton
            size="sm"
            icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            variant="ghost"
            aria-label={isExpanded ? "Minimize" : "Expand"}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          />
        </HStack>
      </Flex>

      {/* Chat Interface */}
      <Collapse in={isExpanded}>
        <VStack
          spacing={0}
          flex="1"
          minH="0"
          h="100%"
          {...(fullHeight
            ? { flex: "1", display: "flex", flexDirection: "column" }
            : { h: "400px", flexShrink: 0, flexGrow: 0 })}
        >
          {/* Chat Tabs */}
          <Box
            w="full"
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="gray.50"
            px={2}
            py={1}
            flexShrink={0}
            flexGrow={0}
            minH="40px"
            maxH="40px"
            overflow="hidden"
          >
            <HStack
              spacing={1}
              overflowX="auto"
              overflowY="hidden"
              w="full"
              maxW="100%"
              justify="space-between"
            >
              <HStack
                spacing={1}
                overflowX="auto"
                overflowY="hidden"
                w="90%"
                maxW="90%"
                flexShrink={1}
              >
                {threads
                  .filter((thread) => {
                    // During shifting phase, keep closed tab in DOM (collapsed). After resizing, hide it.
                    if (
                      tabAnimationPhase === "shifting" &&
                      recentlyClosedThreads.has(thread.thread_id)
                    ) {
                      return true;
                    }
                    return !closedThreads.has(thread.thread_id);
                  })
                  .map((thread) => {
                    const isClosed =
                      recentlyClosedThreads.has(thread.thread_id) &&
                      tabAnimationPhase === "shifting";

                    // Calculate how many closed tabs are before this one in the original threads array
                    const threadIndex = threads.findIndex(
                      (t) => t.thread_id === thread.thread_id
                    );

                    // Count consecutive closed tabs immediately before this one
                    let consecutiveClosedCount = 0;
                    for (let i = threadIndex - 1; i >= 0; i--) {
                      if (
                        recentlyClosedThreads.has(threads[i].thread_id) &&
                        tabAnimationPhase === "shifting"
                      ) {
                        consecutiveClosedCount++;
                      } else {
                        break;
                      }
                    }

                    // Calculate width: during shifting, closed tab has width 0, others keep original.
                    // During resizing, distribute evenly among remaining tabs, but allow expansion up to 90% of bar.
                    let tabWidth;
                    let maxTabWidth = "90%";
                    let transformX = "0px";

                    if (isClosed) {
                      // Closed tab: collapse to width 0 during shifting phase
                      tabWidth = "0px";
                      maxTabWidth = "0px";
                    } else if (tabAnimationPhase === "shifting") {
                      // During shifting, other tabs keep their original width
                      const allVisibleThreads = threads.filter(
                        (t) =>
                          !closedThreads.has(t.thread_id) ||
                          (recentlyClosedThreads.has(t.thread_id) &&
                            tabAnimationPhase === "shifting")
                      );
                      const closedCount = Array.from(allVisibleThreads).filter(
                        (t) => recentlyClosedThreads.has(t.thread_id)
                      ).length;
                      const openCount = allVisibleThreads.length - closedCount;
                      tabWidth =
                        openCount > 0
                          ? `${90 / allVisibleThreads.length}%`
                          : "auto";

                      // Calculate transform to shift left by the width of closed tabs before this one
                      // Only shift by consecutive closed tabs immediately before to prevent overlap
                      if (consecutiveClosedCount > 0) {
                        const tabWidthPercent = 90 / allVisibleThreads.length;
                        // Shift by consecutive closed tabs only, ensuring we don't overlap with previous open tab
                        transformX = `calc(-1 * ${tabWidthPercent}% * ${consecutiveClosedCount})`;
                      }
                    } else {
                      // Resize to fill space after shifting
                      const openThreads = threads.filter(
                        (t) => !closedThreads.has(t.thread_id)
                      );
                      tabWidth =
                        openThreads.length > 0
                          ? `${90 / openThreads.length}%`
                          : "auto";
                    }

                    return (
                      <Button
                        key={thread.thread_id}
                        size="sm"
                        variant={
                          activeThreadId === thread.thread_id
                            ? "solid"
                            : "ghost"
                        }
                        colorScheme={
                          activeThreadId === thread.thread_id ? "blue" : "gray"
                        }
                        onClick={() => switchThread(thread.thread_id)}
                        minW={tabWidth}
                        maxW={maxTabWidth}
                        w={tabWidth}
                        px={isClosed ? 0 : 2}
                        pr={isClosed ? 0 : 1}
                        flexShrink={0}
                        flexGrow={0}
                        opacity={isClosed ? 0 : 1}
                        overflow="hidden"
                        transform={
                          transformX !== "0px"
                            ? `translateX(${transformX})`
                            : "none"
                        }
                        transition={
                          tabAnimationPhase === "shifting"
                            ? "min-width 0.3s ease-out, max-width 0.3s ease-out, opacity 0.3s ease-out, padding 0.3s ease-out, transform 0.3s ease-out"
                            : tabAnimationPhase === "resizing"
                            ? "min-width 0.3s ease-out, max-width 0.3s ease-out, transform 0.3s ease-out"
                            : "none"
                        }
                        pointerEvents={isClosed ? "none" : "auto"}
                      >
                        <HStack spacing={1} w="full" justify="space-between">
                          <Text fontSize="sm" isTruncated>
                            Chat {thread.thread_id}
                          </Text>
                          <Box
                            as="button"
                            aria-label="Close thread"
                            onClick={(e) => closeThread(thread.thread_id, e)}
                            fontSize="xs"
                            lineHeight="1"
                            p={0}
                            m={0}
                            minW="12px"
                            h="12px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            _hover={{ opacity: 0.8 }}
                            flexShrink={0}
                          >
                            <CloseIcon boxSize="8px" />
                          </Box>
                        </HStack>
                      </Button>
                    );
                  })}
                {!isLoadingThreads && (
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={createNewThread}
                    minW="auto"
                    px={2}
                    title="New Chat"
                    flexShrink={0}
                  >
                    +
                  </Button>
                )}
              </HStack>
              {threads.length > 0 && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="View all threads"
                    icon={<HamburgerIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    flexShrink={0}
                    alignSelf="flex-start"
                    mt="2px"
                  />
                  <MenuList maxH="300px" overflowY="auto">
                    {threads.map((thread) => {
                      const isClosed = closedThreads.has(thread.thread_id);
                      return (
                        <MenuItem
                          key={thread.thread_id}
                          onClick={() => switchThread(thread.thread_id)}
                          bg={
                            activeThreadId === thread.thread_id
                              ? "blue.50"
                              : "transparent"
                          }
                        >
                          <HStack w="full" justify="space-between">
                            <Text>
                              Chat {thread.thread_id}
                              {isClosed && (
                                <Text
                                  as="span"
                                  fontSize="xs"
                                  color="gray.500"
                                  ml={2}
                                >
                                  (closed)
                                </Text>
                              )}
                            </Text>
                          </HStack>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </Box>

          {/* Messages */}
          <Box
            flex="1"
            overflowY="auto"
            h="100%"
            minH="0"
            p={4}
            w="full"
            {...(!fullHeight && { maxH: "300px" })}
            ref={messagesContainerRef}
          >
            <VStack spacing={0} align="stretch">
              {messages.map(renderMessage)}
              {isLoading && (
                <Box alignSelf="flex-start" mb={4}>
                  <HStack spacing={2}>
                    <Spinner size="sm" color="blue.500" />
                    <Text fontSize="sm" color="gray.500">
                      AI is thinking...
                    </Text>
                  </HStack>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </VStack>
          </Box>

          <Divider />

          {/* Source Selection */}
          <Box p={3} w="full" bg="gray.50">
            <HStack
              justify="space-between"
              align="center"
              mb={showSources ? 2 : 0}
            >
              <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                Include Sources:
              </Text>
              <IconButton
                size="xs"
                icon={showSources ? <ChevronUpIcon /> : <ChevronDownIcon />}
                variant="ghost"
                aria-label={showSources ? "Hide sources" : "Show sources"}
                onClick={() => setShowSources(!showSources)}
                colorScheme="gray"
              />
            </HStack>
            <Collapse in={showSources}>
              <CheckboxGroup
                value={selectedSources}
                onChange={handleSourcesChange}
                size="sm"
              >
                <HStack spacing={4} flexWrap="wrap" align="flex-start">
                  {!isMagicNotes && <Checkbox value={entityInfoSource} isDisabled={isLoading}>
                    {getLabel(userRole, 'entity')} Info
                  </Checkbox>}
                  <Checkbox
                    value={documentSource}
                    isDisabled={isLoading || !documentContent}
                    flexShrink={0}
                  >
                    {isMagicNotes ? "Magic Notes" : getLabel(userRole, 'documentName')}
                  </Checkbox>
                  {!isMagicNotes && <VStack spacing={1} align="flex-start" flexShrink={0} w="max-content" minW={0} alignSelf="flex-start">
                    <HStack spacing={0} align="center">
                      <Checkbox value="uploaded_files" isDisabled={isLoading || !hasReports}>
                        Reports
                      </Checkbox>
                      {hasReports && reportFiles.length > 1 && (
                        <IconButton
                          size="sm"
                          icon={showReportDropdown ? <ChevronDownIcon boxSize={5} /> : <ChevronRightIcon boxSize={5} />}
                          variant="ghost"
                          aria-label="Select specific reports"
                          onClick={(e) => { e.stopPropagation(); aiLog("📂 Reports arrow clicked:", { patientId, effectiveDate, reportFiles, selectedReportFiles, rawPath: (Array.isArray(patientData) ? patientData[0] : patientData)?.reports }); setShowReportDropdown(d => !d); }}
                          colorScheme="gray"
                          fontWeight="bold"
                          minW={8}
                          minH={8}
                        />
                      )}
                    </HStack>
                    {showReportDropdown && reportFiles.length > 1 && (
                      <Box pl={2} borderLeft="2px solid" borderColor="blue.200">
                        <VStack align="flex-start" spacing={0.5}>
                          {reportFiles.map(file => (
                            <Checkbox
                              key={file.name}
                              size="sm"
                              isChecked={selectedReportFiles.includes(file.name)}
                              onChange={(e) => {
                                const newFiles = e.target.checked
                                  ? [...selectedReportFiles, file.name]
                                  : selectedReportFiles.filter(n => n !== file.name);
                                setSelectedReportFiles(newFiles);
                                if (newFiles.length === 0) {
                                  setSelectedSources(s => s.filter(s => s !== "uploaded_files"));
                                } else if (e.target.checked && !selectedSources.includes("uploaded_files")) {
                                  setSelectedSources(s => [...s, "uploaded_files"]);
                                }
                              }}
                              isDisabled={isLoading}
                            >
                              <Text fontSize="xs">{file.name}</Text>
                            </Checkbox>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>}
                  {!isMagicNotes && <Checkbox value="visit_history" isDisabled={isLoading} flexShrink={0}>
                    {historyLabel}
                  </Checkbox>}
                  {!isMagicNotes && canAccessComponent(userRole, 'aiAssistant.formsSource') && (
                    <Checkbox
                      value="forms"
                      isDisabled={isLoading || !hasFormsData}
                    >
                      Forms
                    </Checkbox>
                  )}
                  {!isMagicNotes && <Checkbox value="audio_transcripts" isDisabled={isLoading}>
                    Audio/Transcripts
                  </Checkbox>}
                  <Checkbox value="ai_knowledge" isDisabled={isLoading} flexShrink={0}>
                    AI Knowledge
                  </Checkbox>
                </HStack>
              </CheckboxGroup>
            </Collapse>
          </Box>

          {/* Feedback Section - Outside chat window, above input */}
          {(() => {
            // Find the last assistant message that can receive feedback
            const lastFeedbackMessage = [...messages]
              .reverse()
              .find(
                (m) =>
                  m.type === "assistant" &&
                  m.id !== "welcome" &&
                  m.prompt &&
                  !m.isError
              );

            if (!lastFeedbackMessage) return null;

            return (
              <Box
                p={3}
                w="full"
                borderTop="1px solid"
                borderColor="gray.200"
                bg="blue.50"
              >
                <HStack
                  justify="space-between"
                  align="center"
                  mb={showFeedback ? 2 : 0}
                >
                  <Text fontSize="sm" color="gray.700" fontWeight="semibold">
                    💬 Was this response helpful?
                  </Text>
                  <IconButton
                    size="xs"
                    icon={
                      showFeedback ? <ChevronUpIcon /> : <ChevronDownIcon />
                    }
                    variant="ghost"
                    aria-label={
                      showFeedback ? "Hide feedback" : "Show feedback"
                    }
                    onClick={() => setShowFeedback(!showFeedback)}
                    colorScheme="gray"
                  />
                </HStack>
                <Collapse in={showFeedback}>
                  <VStack align="flex-start" spacing={3} w="full">
                    <HStack spacing={3} w="full">
                      <Button
                        size="sm"
                        variant={
                          feedbackStatus[lastFeedbackMessage.id] === "positive"
                            ? "solid"
                            : "outline"
                        }
                        colorScheme={
                          feedbackStatus[lastFeedbackMessage.id] === "positive"
                            ? "green"
                            : "gray"
                        }
                        onClick={() =>
                          handleFeedbackClick(
                            lastFeedbackMessage.id,
                            "positive"
                          )
                        }
                        isDisabled={submittingFeedback[lastFeedbackMessage.id]}
                        leftIcon={<span>👍</span>}
                      >
                        Helpful
                      </Button>
                      <Button
                        size="sm"
                        variant={
                          feedbackStatus[lastFeedbackMessage.id] === "negative"
                            ? "solid"
                            : "outline"
                        }
                        colorScheme={
                          feedbackStatus[lastFeedbackMessage.id] === "negative"
                            ? "red"
                            : "gray"
                        }
                        onClick={() =>
                          handleFeedbackClick(
                            lastFeedbackMessage.id,
                            "negative"
                          )
                        }
                        isDisabled={submittingFeedback[lastFeedbackMessage.id]}
                        leftIcon={<span>👎</span>}
                      >
                        Not Helpful
                      </Button>
                    </HStack>

                    {/* Feedback text input - shown when thumbs up/down is clicked */}
                    {showFeedbackInput[lastFeedbackMessage.id] && (
                      <VStack align="flex-start" spacing={2} w="full" mt={2}>
                        <Textarea
                          size="sm"
                          placeholder="Optional: Tell us more about your feedback..."
                          value={feedbackText[lastFeedbackMessage.id] || ""}
                          onChange={(e) =>
                            setFeedbackText((prev) => ({
                              ...prev,
                              [lastFeedbackMessage.id]: e.target.value,
                            }))
                          }
                          rows={2}
                          resize="none"
                          isDisabled={
                            submittingFeedback[lastFeedbackMessage.id]
                          }
                        />
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => {
                              const prompt = lastFeedbackMessage.prompt || "";
                              handleSubmitFeedback(
                                lastFeedbackMessage.id,
                                prompt,
                                lastFeedbackMessage.content
                              );
                            }}
                            isLoading={
                              submittingFeedback[lastFeedbackMessage.id]
                            }
                            loadingText="Submitting..."
                          >
                            Submit Feedback
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setShowFeedbackInput((prev) => ({
                                ...prev,
                                [lastFeedbackMessage.id]: false,
                              }));
                              setFeedbackStatus((prev) => {
                                const newStatus = { ...prev };
                                delete newStatus[lastFeedbackMessage.id];
                                return newStatus;
                              });
                              setFeedbackText((prev) => {
                                const newText = { ...prev };
                                delete newText[lastFeedbackMessage.id];
                                return newText;
                              });
                            }}
                            isDisabled={
                              submittingFeedback[lastFeedbackMessage.id]
                            }
                          >
                            Cancel
                          </Button>
                        </HStack>
                      </VStack>
                    )}
                  </VStack>
                </Collapse>
              </Box>
            );
          })()}

          {/* Input */}
          <Box p={4} w="full">
            <HStack spacing={2}>
              <Textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about this patient's medical records..."
                size="sm"
                resize="none"
                rows={2}
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 1px #3182ce",
                }}
              />
              {isLoading ? (
                <Button
                  colorScheme="red"
                  onClick={stopCurrentRequest}
                  size="sm"
                  minW="40px"
                  height="40px"
                  px={0}
                  borderRadius="md"
                  title="Stop"
                >
                  ■
                </Button>
              ) : (
                <Button
                  colorScheme="blue"
                  onClick={handleSendMessage}
                  isDisabled={!inputMessage.trim()}
                  size="sm"
                  px={6}
                >
                  Send
                </Button>
              )}
            </HStack>
          </Box>
        </VStack>
      </Collapse>

      {/* Draggable Pop-out Window */}
      {(isPopOutOpen || isGlobalPopup) && (
        <Portal>
          <style>{resizeHandleStyle}</style>
          <Box
            ref={popupRef}
            position="fixed"
            left={`${position.x}px`}
            top={`${position.y}px`}
            zIndex={9999}
          >
            <ResizableBox
              width={popupSize.width}
              height={isPopupMinimized ? 60 : popupSize.height}
              minConstraints={[400, isPopupMinimized ? 60 : 400]}
              maxConstraints={[Infinity, Infinity]}
              resizeHandles={isPopupMinimized ? [] : ["se", "sw", "ne", "nw"]}
              onResizeStart={handleResizeStart}
              onResize={handleResize}
              onResizeStop={handleResizeStop}
              style={{
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                border: "1px solid",
                borderColor: "#cbd5e0",
                cursor: isDragging ? "grabbing" : "default",
              }}
            >
              <Box
                w="100%"
                h="100%"
                display="flex"
                flexDirection="column"
                overflow="hidden"
              >
                {/* Draggable Header */}
                <Flex
                  align="center"
                  justify="space-between"
                  p={4}
                  bg="blue.50"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  cursor={isDragging ? "grabbing" : "grab"}
                  _active={{ cursor: "grabbing" }}
                  onMouseDown={handleHeaderMouseDown}
                  userSelect="none"
                >
                  <HStack spacing={3} flex="1">
                    <ChatIcon color="blue.500" />
                    <Heading size="md" color="blue.700">
                      AI Assistant
                    </Heading>
                    <Text fontSize="md" color="blue.600" fontWeight="medium">
                      Chat {activeThreadId}
                    </Text>
                    {messages.length > 1 && (
                      <Badge colorScheme="blue" size="sm">
                        {messages.filter((m) => m.type === "user").length}{" "}
                        queries
                      </Badge>
                    )}
                    {renderSkillHeaderBadge()}
                  </HStack>
                  <HStack spacing={1}>
                    <IconButton
                      size="sm"
                      icon={<SettingsIcon />}
                      variant="ghost"
                      aria-label="Settings"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSettingsOpen(true);
                        setIsSettingsUnlocked(false);
                        setSettingsPassword("");
                      }}
                      colorScheme="blue"
                      onMouseDown={(e) => e.stopPropagation()}
                      title="Settings"
                    />
                    <IconButton
                      size="sm"
                      icon={<ArrowUpIcon />}
                      variant="ghost"
                      aria-label="Move to side"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveToSide();
                      }}
                      colorScheme="blue"
                      onMouseDown={(e) => e.stopPropagation()}
                      title="Move to side"
                    />
                    <IconButton
                      size="sm"
                      icon={
                        isPopupMinimized ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      aria-label={isPopupMinimized ? "Maximize" : "Minimize"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePopupMinimize();
                      }}
                      colorScheme="blue"
                      onMouseDown={(e) => e.stopPropagation()}
                      title={isPopupMinimized ? "Maximize" : "Minimize"}
                    />
                    <IconButton
                      size="sm"
                      icon={<CloseIcon />}
                      variant="ghost"
                      aria-label="Close"
                      onClick={isGlobalPopup ? handleClose : onPopOutClose}
                      colorScheme="blue"
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  </HStack>
                </Flex>

                {/* Chat Interface - Only show when not minimized */}
                {!isPopupMinimized && (
                  <VStack spacing={0} h="100%" flex="1" overflow="hidden">
                    {/* Chat Tabs */}
                    <Box
                      w="full"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                      bg="gray.50"
                      px={2}
                      py={1}
                      onMouseDown={(e) => e.stopPropagation()}
                      flexShrink={0}
                      flexGrow={0}
                      minH="40px"
                      maxH="40px"
                      overflow="hidden"
                    >
                      <HStack
                        spacing={1}
                        overflowX="auto"
                        overflowY="hidden"
                        w="full"
                        maxW="100%"
                      >
                        <HStack
                          spacing={1}
                          overflowX="auto"
                          overflowY="hidden"
                          w="90%"
                          maxW="90%"
                          flexShrink={1}
                        >
                          {threads
                            .filter((thread) => {
                              // During shifting phase, keep closed tab in DOM (collapsed). After resizing, hide it.
                              if (
                                tabAnimationPhase === "shifting" &&
                                recentlyClosedThreads.has(thread.thread_id)
                              ) {
                                return true;
                              }
                              return !closedThreads.has(thread.thread_id);
                            })
                            .map((thread) => {
                              const isClosed =
                                recentlyClosedThreads.has(thread.thread_id) &&
                                tabAnimationPhase === "shifting";

                              // Calculate how many closed tabs are before this one in the original threads array
                              const threadIndex = threads.findIndex(
                                (t) => t.thread_id === thread.thread_id
                              );

                              // Count consecutive closed tabs immediately before this one
                              let consecutiveClosedCount = 0;
                              for (let i = threadIndex - 1; i >= 0; i--) {
                                if (
                                  recentlyClosedThreads.has(
                                    threads[i].thread_id
                                  ) &&
                                  tabAnimationPhase === "shifting"
                                ) {
                                  consecutiveClosedCount++;
                                } else {
                                  break;
                                }
                              }

                              // Calculate width: during shifting, closed tab has width 0, others keep original.
                              // During resizing, distribute evenly among remaining tabs.
                              let tabWidth;
                              let maxTabWidth = "90%";
                              let transformX = "0px";

                              if (isClosed) {
                                // Closed tab: collapse to width 0 during shifting phase
                                tabWidth = "0px";
                                maxTabWidth = "0px";
                              } else if (tabAnimationPhase === "shifting") {
                                // During shifting, other tabs keep their original width
                                const allVisibleThreads = threads.filter(
                                  (t) =>
                                    !closedThreads.has(t.thread_id) ||
                                    (recentlyClosedThreads.has(t.thread_id) &&
                                      tabAnimationPhase === "shifting")
                                );
                                const closedCount = Array.from(
                                  allVisibleThreads
                                ).filter((t) =>
                                  recentlyClosedThreads.has(t.thread_id)
                                ).length;
                                const openCount =
                                  allVisibleThreads.length - closedCount;
                                tabWidth =
                                  openCount > 0
                                    ? `${90 / allVisibleThreads.length}%`
                                    : "auto";

                                // Calculate transform to shift left by the width of closed tabs before this one
                                // Only shift by consecutive closed tabs immediately before to prevent overlap
                                if (consecutiveClosedCount > 0) {
                                  const tabWidthPercent =
                                    90 / allVisibleThreads.length;
                                  // Shift by consecutive closed tabs only, ensuring we don't overlap with previous open tab
                                  transformX = `calc(-1 * ${tabWidthPercent}% * ${consecutiveClosedCount})`;
                                }
                              } else {
                                // Resize to fill space after shifting
                                const openThreads = threads.filter(
                                  (t) => !closedThreads.has(t.thread_id)
                                );
                                tabWidth =
                                  openThreads.length > 0
                                    ? `${90 / openThreads.length}%`
                                    : "auto";
                              }

                              return (
                                <Button
                                  key={thread.thread_id}
                                  size="sm"
                                  variant={
                                    activeThreadId === thread.thread_id
                                      ? "solid"
                                      : "ghost"
                                  }
                                  colorScheme={
                                    activeThreadId === thread.thread_id
                                      ? "blue"
                                      : "gray"
                                  }
                                  onClick={() => switchThread(thread.thread_id)}
                                  minW={tabWidth}
                                  maxW={maxTabWidth}
                                  w={tabWidth}
                                  px={isClosed ? 0 : 2}
                                  pr={isClosed ? 0 : 1}
                                  flexShrink={0}
                                  flexGrow={0}
                                  opacity={isClosed ? 0 : 1}
                                  overflow="hidden"
                                  transform={
                                    transformX !== "0px"
                                      ? `translateX(${transformX})`
                                      : "none"
                                  }
                                  transition={
                                    tabAnimationPhase === "shifting"
                                      ? "min-width 0.3s ease-out, max-width 0.3s ease-out, opacity 0.3s ease-out, padding 0.3s ease-out, transform 0.3s ease-out"
                                      : tabAnimationPhase === "resizing"
                                      ? "min-width 0.3s ease-out, max-width 0.3s ease-out, transform 0.3s ease-out"
                                      : "none"
                                  }
                                  pointerEvents={isClosed ? "none" : "auto"}
                                >
                                  <HStack
                                    spacing={1}
                                    w="full"
                                    justify="space-between"
                                  >
                                    <Text fontSize="sm" isTruncated>
                                      Chat {thread.thread_id}
                                    </Text>
                                    <Box
                                      as="button"
                                      aria-label="Close thread"
                                      onClick={(e) =>
                                        closeThread(thread.thread_id, e)
                                      }
                                      fontSize="xs"
                                      lineHeight="1"
                                      p={0}
                                      m={0}
                                      minW="12px"
                                      h="12px"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      _hover={{ opacity: 0.8 }}
                                      flexShrink={0}
                                    >
                                      <CloseIcon boxSize="8px" />
                                    </Box>
                                  </HStack>
                                </Button>
                              );
                            })}
                          {!isLoadingThreads && (
                            <Button
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={createNewThread}
                              minW="auto"
                              px={2}
                              title="New Chat"
                              flexShrink={0}
                            >
                              +
                            </Button>
                          )}
                        </HStack>
                        {threads.length > 0 && (
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              aria-label="View all threads"
                              icon={<HamburgerIcon />}
                              size="sm"
                              variant="ghost"
                              colorScheme="gray"
                              flexShrink={0}
                            />
                            <MenuList maxH="300px" overflowY="auto">
                              {threads.map((thread) => {
                                const isClosed = closedThreads.has(
                                  thread.thread_id
                                );
                                return (
                                  <MenuItem
                                    key={thread.thread_id}
                                    onClick={() =>
                                      switchThread(thread.thread_id)
                                    }
                                    bg={
                                      activeThreadId === thread.thread_id
                                        ? "blue.50"
                                        : "transparent"
                                    }
                                  >
                                    <HStack w="full" justify="space-between">
                                      <Text>
                                        Chat {thread.thread_id}
                                        {isClosed && (
                                          <Text
                                            as="span"
                                            fontSize="xs"
                                            color="gray.500"
                                            ml={2}
                                          >
                                            (closed)
                                          </Text>
                                        )}
                                      </Text>
                                    </HStack>
                                  </MenuItem>
                                );
                              })}
                            </MenuList>
                          </Menu>
                        )}
                      </HStack>
                    </Box>

                    {/* Messages */}
                    <Box
                      flex="1"
                      overflowY="auto"
                      p={4}
                      w="full"
                      ref={modalMessagesContainerRef}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <VStack spacing={0} align="stretch">
                        {messages.map(renderMessage)}
                        {isLoading && (
                          <Box alignSelf="flex-start" mb={4}>
                            <HStack spacing={2}>
                              <Spinner size="sm" color="blue.500" />
                              <Text fontSize="sm" color="gray.500">
                                AI is thinking...
                              </Text>
                            </HStack>
                          </Box>
                        )}
                        <div ref={messagesEndRef} />
                      </VStack>
                    </Box>

                    <Divider />

                    {/* Feedback Section - Outside chat window, above input */}
                    {(() => {
                      // Find the last assistant message (error or not)
                      const lastAssistantMessage = [...messages]
                        .reverse()
                        .find(
                          (m) => m.type === "assistant" && m.id !== "welcome"
                        );

                      if (!lastAssistantMessage) return null;

                      const canShowFeedback =
                        lastAssistantMessage.prompt &&
                        !lastAssistantMessage.isError;
                      const lastUserMessage = [...messages]
                        .reverse()
                        .find((m) => m.type === "user");

                      return (
                        <Box
                          p={3}
                          w="full"
                          borderTop="1px solid"
                          borderColor="gray.200"
                          bg={
                            lastAssistantMessage.isError ? "red.50" : "blue.50"
                          }
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <HStack
                            justify="space-between"
                            align="center"
                            mb={showFeedback && canShowFeedback ? 2 : 0}
                          >
                            {canShowFeedback ? (
                              <Text
                                fontSize="sm"
                                color="gray.700"
                                fontWeight="semibold"
                              >
                                💬 Was this response helpful?
                              </Text>
                            ) : (
                              <Text
                                fontSize="sm"
                                color={
                                  lastAssistantMessage.isError
                                    ? "red.600"
                                    : "gray.600"
                                }
                                fontWeight="semibold"
                              >
                                {lastAssistantMessage.isError
                                  ? "⚠️ Response generation failed"
                                  : "Response generated"}
                              </Text>
                            )}

                            <HStack spacing={2}>
                              {lastUserMessage && (
                                <Button
                                  size="xs"
                                  variant="outline"
                                  colorScheme={
                                    lastAssistantMessage.isError
                                      ? "red"
                                      : "blue"
                                  }
                                  leftIcon={<RepeatIcon />}
                                  onClick={() =>
                                    handleSendMessage(lastUserMessage.content)
                                  }
                                  isDisabled={isLoading}
                                  bg="white"
                                >
                                  Try Again
                                </Button>
                              )}

                              {canShowFeedback && (
                                <IconButton
                                  size="xs"
                                  icon={
                                    showFeedback ? (
                                      <ChevronUpIcon />
                                    ) : (
                                      <ChevronDownIcon />
                                    )
                                  }
                                  variant="ghost"
                                  aria-label={
                                    showFeedback
                                      ? "Hide feedback"
                                      : "Show feedback"
                                  }
                                  onClick={() => setShowFeedback(!showFeedback)}
                                  colorScheme="gray"
                                />
                              )}
                            </HStack>
                          </HStack>

                          {canShowFeedback && (
                            <Collapse in={showFeedback}>
                              <VStack align="flex-start" spacing={3} w="full">
                                <HStack spacing={3} w="full">
                                  <Button
                                    size="sm"
                                    variant={
                                      feedbackStatus[
                                        lastAssistantMessage.id
                                      ] === "positive"
                                        ? "solid"
                                        : "outline"
                                    }
                                    colorScheme={
                                      feedbackStatus[
                                        lastAssistantMessage.id
                                      ] === "positive"
                                        ? "green"
                                        : "gray"
                                    }
                                    onClick={() =>
                                      handleFeedbackClick(
                                        lastAssistantMessage.id,
                                        "positive"
                                      )
                                    }
                                    isDisabled={
                                      submittingFeedback[
                                        lastAssistantMessage.id
                                      ]
                                    }
                                    leftIcon={<span>👍</span>}
                                  >
                                    Helpful
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={
                                      feedbackStatus[
                                        lastAssistantMessage.id
                                      ] === "negative"
                                        ? "solid"
                                        : "outline"
                                    }
                                    colorScheme={
                                      feedbackStatus[
                                        lastAssistantMessage.id
                                      ] === "negative"
                                        ? "red"
                                        : "gray"
                                    }
                                    onClick={() =>
                                      handleFeedbackClick(
                                        lastAssistantMessage.id,
                                        "negative"
                                      )
                                    }
                                    isDisabled={
                                      submittingFeedback[
                                        lastAssistantMessage.id
                                      ]
                                    }
                                    leftIcon={<span>👎</span>}
                                  >
                                    Not Helpful
                                  </Button>
                                </HStack>

                                {/* Feedback text input - shown when thumbs up/down is clicked */}
                                {showFeedbackInput[lastAssistantMessage.id] && (
                                  <VStack
                                    align="flex-start"
                                    spacing={2}
                                    w="full"
                                    mt={2}
                                  >
                                    <Textarea
                                      size="sm"
                                      placeholder="Optional: Tell us more about your feedback..."
                                      value={
                                        feedbackText[lastAssistantMessage.id] ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setFeedbackText((prev) => ({
                                          ...prev,
                                          [lastAssistantMessage.id]:
                                            e.target.value,
                                        }))
                                      }
                                      rows={2}
                                      resize="none"
                                      isDisabled={
                                        submittingFeedback[
                                          lastAssistantMessage.id
                                        ]
                                      }
                                    />
                                    <HStack spacing={2}>
                                      <Button
                                        size="sm"
                                        colorScheme="blue"
                                        onClick={() => {
                                          const prompt =
                                            lastAssistantMessage.prompt || "";
                                          handleSubmitFeedback(
                                            lastAssistantMessage.id,
                                            prompt,
                                            lastAssistantMessage.content
                                          );
                                        }}
                                        isLoading={
                                          submittingFeedback[
                                            lastAssistantMessage.id
                                          ]
                                        }
                                        loadingText="Submitting..."
                                      >
                                        Submit Feedback
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          setShowFeedbackInput((prev) => ({
                                            ...prev,
                                            [lastAssistantMessage.id]: false,
                                          }));
                                          setFeedbackStatus((prev) => {
                                            const newStatus = { ...prev };
                                            delete newStatus[
                                              lastAssistantMessage.id
                                            ];
                                            return newStatus;
                                          });
                                          setFeedbackText((prev) => {
                                            const newText = { ...prev };
                                            delete newText[
                                              lastAssistantMessage.id
                                            ];
                                            return newText;
                                          });
                                        }}
                                        isDisabled={
                                          submittingFeedback[
                                            lastAssistantMessage.id
                                          ]
                                        }
                                      >
                                        Cancel
                                      </Button>
                                    </HStack>
                                  </VStack>
                                )}
                              </VStack>
                            </Collapse>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Source Selection */}
                    <Box
                      p={3}
                      w="full"
                      borderTop="1px solid"
                      borderColor="gray.200"
                      bg="gray.50"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <HStack
                        justify="space-between"
                        align="center"
                        mb={showSources ? 2 : 0}
                      >
                        <Text
                          fontSize="xs"
                          color="gray.600"
                          fontWeight="semibold"
                        >
                          Include Sources:
                        </Text>
                        <IconButton
                          size="xs"
                          icon={
                            showSources ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          variant="ghost"
                          aria-label={
                            showSources ? "Hide sources" : "Show sources"
                          }
                          onClick={() => setShowSources(!showSources)}
                          colorScheme="gray"
                        />
                      </HStack>
                      <Collapse in={showSources}>
                        <CheckboxGroup
                          value={selectedSources}
                          onChange={handleSourcesChange}
                          size="sm"
                        >
                          <HStack spacing={4} flexWrap="wrap" align="flex-start">
                            {!isMagicNotes && <Checkbox
                              value={entityInfoSource}
                              isDisabled={isLoading}
                              flexShrink={0}
                            >
                              {getLabel(userRole, 'entity')} Info
                            </Checkbox>}
                            <Checkbox
                              value={documentSource}
                              isDisabled={isLoading || !documentContent}
                              flexShrink={0}
                            >
                              {isMagicNotes ? "Magic Notes" : getLabel(userRole, 'documentName')}
                            </Checkbox>
                            {!isMagicNotes && <VStack spacing={1} align="flex-start" flexShrink={0} w="max-content" minW={0} alignSelf="flex-start">
                              <HStack spacing={0} align="center">
                                <Checkbox
                                  value="uploaded_files"
                                  isDisabled={isLoading || !hasReports}
                                >
                                  Reports
                                </Checkbox>
                                {hasReports && reportFiles.length > 1 && (
                                  <IconButton
                                    size="sm"
                                    icon={showReportDropdown ? <ChevronDownIcon boxSize={5} /> : <ChevronRightIcon boxSize={5} />}
                                    variant="ghost"
                                    aria-label="Select specific reports"
                                    onClick={(e) => { e.stopPropagation(); aiLog("📂 Reports arrow clicked:", { patientId, effectiveDate, reportFiles, selectedReportFiles, rawPath: (Array.isArray(patientData) ? patientData[0] : patientData)?.reports }); setShowReportDropdown(d => !d); }}
                                    colorScheme="gray"
                                    fontWeight="bold"
                                    minW={8}
                                    minH={8}
                                  />
                                )}
                              </HStack>
                              {showReportDropdown && reportFiles.length > 1 && (
                                <Box pl={2} borderLeft="2px solid" borderColor="blue.200">
                                  <VStack align="flex-start" spacing={0.5}>
                                    {reportFiles.map(file => (
                                      <Checkbox
                                        key={file.name}
                                        size="sm"
                                        isChecked={selectedReportFiles.includes(file.name)}
                                        onChange={(e) => {
                                          const newFiles = e.target.checked
                                            ? [...selectedReportFiles, file.name]
                                            : selectedReportFiles.filter(n => n !== file.name);
                                          setSelectedReportFiles(newFiles);
                                          if (newFiles.length === 0) {
                                            setSelectedSources(s => s.filter(s => s !== "uploaded_files"));
                                          } else if (e.target.checked && !selectedSources.includes("uploaded_files")) {
                                            setSelectedSources(s => [...s, "uploaded_files"]);
                                          }
                                        }}
                                        isDisabled={isLoading}
                                      >
                                        <Text fontSize="xs">{file.name}</Text>
                                      </Checkbox>
                                    ))}
                                  </VStack>
                                </Box>
                              )}
                            </VStack>}
                            {!isMagicNotes && <Checkbox
                              value="visit_history"
                              isDisabled={isLoading}
                              flexShrink={0}
                            >
                              {historyLabel}
                            </Checkbox>}
                            {!isMagicNotes && canAccessComponent(userRole, 'aiAssistant.formsSource') && (
                              <Checkbox
                                value="forms"
                                isDisabled={isLoading || !hasFormsData}
                              >
                                Forms
                              </Checkbox>
                            )}
                            {!isMagicNotes && <Checkbox
                              value="audio_transcripts"
                              isDisabled={isLoading}
                              flexShrink={0}
                            >
                              Audio/Transcripts
                            </Checkbox>}
                            <Checkbox
                              value="ai_knowledge"
                              isDisabled={isLoading}
                              flexShrink={0}
                            >
                              AI Knowledge
                            </Checkbox>
                          </HStack>
                        </CheckboxGroup>
                      </Collapse>
                    </Box>

                    {/* Input */}
                    <Box
                      p={4}
                      w="full"
                      borderTop="1px solid"
                      borderColor="gray.200"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <HStack spacing={2}>
                        <Textarea
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Ask me anything about this patient's medical records..."
                          size="sm"
                          resize="none"
                          rows={2}
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px #3182ce",
                          }}
                        />
                        {isLoading ? (
                          <Button
                            colorScheme="red"
                            onClick={stopCurrentRequest}
                            size="sm"
                            minW="40px"
                            height="40px"
                            px={0}
                            borderRadius="md"
                            title="Stop"
                          >
                            ■
                          </Button>
                        ) : (
                          <Button
                            colorScheme="blue"
                            onClick={handleSendMessage}
                            isDisabled={!inputMessage.trim()}
                            size="sm"
                            px={6}
                          >
                            Send
                          </Button>
                        )}
                      </HStack>
                    </Box>
                  </VStack>
                )}
              </Box>
            </ResizableBox>
          </Box>
        </Portal>
      )}

      {/* Settings Modal */}
      <Modal
        isOpen={isSettingsOpen}
        onClose={() => {
          setIsSettingsOpen(false);
          setIsSettingsUnlocked(false);
          setSettingsPassword("");
          setSettingsPasswordError("");
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>AI Assistant Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!isSettingsUnlocked ? (
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  Enter password to access settings:
                </Text>
                <FormControl isInvalid={!!settingsPasswordError}>
                  <Input
                    type="password"
                    value={settingsPassword}
                    onChange={(e) => {
                      setSettingsPassword(e.target.value);
                      setSettingsPasswordError("");
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSettingsPasswordSubmit();
                      }
                    }}
                    placeholder="Password"
                  />
                  {settingsPasswordError && (
                    <FormErrorMessage>{settingsPasswordError}</FormErrorMessage>
                  )}
                </FormControl>
                <Button
                  colorScheme="blue"
                  onClick={handleSettingsPasswordSubmit}
                >
                  Unlock Settings
                </Button>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                {renderSettingsBody()}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AIAssistant;
